import { Translations } from './types';

export const translationsEn: Translations = {
  landing: {
    signIn: 'Sign in',
    signUp: 'Sign up',
    hero: {
      eyebrow: 'Welcome',
      title: "Let's get you set up",
      subhead:
        "Creating your Data Mission account takes about two minutes. Here's what to expect before you head into the signup flow, so nothing comes as a surprise.",
      illustrationAlt: 'Illustration of a hand connecting a plug to a cloud',
    },
    steps: {
      stepLabel: 'Step',
      step1Title: 'Enter your email address',
      step1Body:
        "We'll use it as your username and send the next step there. Use a work email if you can, since this is the account your team will be tied to.",
      step2Title: 'Verify with a one-time code',
      step2Body:
        'Check your inbox for a six-digit code from us, then paste it back into the signup screen. Takes a few seconds, and confirms the email is yours.',
      step3Title: 'Set your password',
      step3Body:
        "Pick a password and fill in a few basic details about you. This is the account you'll use to sign back in any time you come back.",
      step4Title: 'Complete the guided onboarding',
      step4Body:
        "Once you're in, we'll walk you through connecting your first source and setting up your first sync, step by step. No documentation hunt required.",
    },
    cta: {
      headline: 'Ready when you are',
      subhead:
        "The signup screen is hosted by Microsoft Entra. It looks like a sign-in, but you're in the right place.",
      signUpEmail: 'Sign up with email',
      signUpMicrosoft: 'Sign up with Microsoft',
      microsoftIconAlt: 'Microsoft logo',
    },
    signInCard: {
      text: 'Already have an account?',
      link: 'Sign in instead ›',
    },
    footer: {
      privacyPolicy: 'Privacy Policy',
      termsOfUse: 'Terms of Use',
    },
  },
  support: {
    title: 'Support',
    description: 'Get help with Data Mission Sync, contact your partner and track your support tickets.',
    unavailable: 'Support is temporarily unavailable. Please try again in a few minutes.',
    helpCenter: {
      title: 'Help center',
      description: 'Browse guides and answers to common questions.',
      open: 'Open help center',
    },
    partner: {
      title: 'Your partner',
      description: 'Your organization is supported by this partner.',
      logoAlt: '{name} logo',
      email: 'Email',
      phone: 'Phone',
      website: 'Website',
      location: 'Location',
    },
    visibility: {
      title: 'Visibility',
      withPartner: 'All users of this organization can view and comment on support tickets, including your partner, {name}.',
      withoutPartner: 'All users of this organization can view and comment on support tickets. If a partner is assigned to you, they can too.',
    },
    status: {
      WaitingOnYou: 'Waiting on you',
      WaitingOnYouTooltip: 'Support replied and needs something from you. Your reply sends it back to them.',
      Open: 'Open',
      OpenTooltip: 'Waiting for the support team to pick it up.',
      InProgress: 'In progress',
      InProgressTooltip: 'The support team is working on it.',
      Resolved: 'Resolved',
      ResolvedTooltip: 'The support team has resolved it. Add a comment if you still need help.',
      Closed: 'Closed',
      ClosedTooltip: 'The ticket is closed. You can still add a comment.',
    },
    person: {
      you: 'You',
      teamMember: 'Team member',
      supportTeam: 'Support team',
      aiSummary: 'AI Summary',
      supportRole: 'Support',
    },
    waitingBanner: {
      titleOne: 'Support is waiting on your reply to 1 ticket',
      titleOther: 'Support is waiting on your reply to {count} tickets',
      body: 'Reply to keep things moving.',
      action: 'Show tickets',
    },
    tickets: {
      title: 'Support tickets',
      description: 'Every ticket raised for your organization, by anyone on your team.',
      newTicket: 'New ticket',
      loading: 'Loading tickets...',
      loadFailed: 'Failed to load support tickets',
      emptyTitle: 'No support tickets yet',
      emptyBody: 'Raise a ticket when you need help from the support team, for example with a sync that fails or a question about your setup. Everyone in your organization can see and follow its progress here.',
      tabsLabel: 'Filter tickets',
      tabs: {
        open: 'Open',
        needsReply: 'Needs your reply',
        closed: 'Closed',
      },
      emptyTab: {
        open: 'No open tickets. Raise a new ticket when you need help.',
        needsReply: 'Nothing needs your reply right now.',
        closed: 'No closed tickets yet.',
      },
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {page} of {totalPages}',
      openedBy: 'Opened by {name}',
      commentCount: '{count} comments',
      attachmentCount: '{count} files',
      noActivity: 'No replies yet',
      columns: {
        ticket: 'Ticket',
        type: 'Type',
        category: 'Category',
        status: 'Status',
        lastActivity: 'Last activity',
      },
      noCategory: '—',
      types: {
        Support: 'Support',
        DataWrangler: 'Data Wrangler',
      },
    },
    newTicket: {
      title: 'New support ticket',
      description: 'Describe what you need help with. We\'ll reply on the ticket.',
      back: 'Back to Support',
      typeLabel: 'Type of help',
      typeSupport: 'Get support',
      typeSupportDescription: 'A question or a problem with Data Mission Sync. Bugs never use hours.',
      typeWrangler: 'Data Wrangler',
      typeWranglerDescription: 'Hands-on help with your setup, configuration or data, using your organization\'s hours.',
      wranglerPartnerNote: 'Services work is offered to your partner, {name}, first.',
      wranglerHoursNote: 'You can send the request whatever your organization\'s balance of hours.',
      titleLabel: 'Title',
      titlePlaceholder: 'A short summary of the problem',
      titleRequired: 'Title is required',
      titleTooLong: 'Title must be {max} characters or fewer',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'What happened, what you expected, and any steps to reproduce it',
      descriptionHint: 'Include the profile name, the table and roughly when it started. Screenshots and error details help most.',
      descriptionRequired: 'Description is required',
      attachmentsLabel: 'Attachments (optional)',
      attachmentsHint: 'Up to {size} per file.',
      attachmentsAllowedHint: 'Allowed file types: {types}.',
      attachmentsBlockedHint: 'These file types can\'t be attached: {types}.',
      removeFile: 'Remove {name}',
      submit: 'Create ticket',
      submitting: 'Creating...',
      cancel: 'Cancel',
      created: 'Support ticket created',
      createFailed: 'Failed to create the support ticket',
      uploadsFailed: 'The ticket was created, but these files could not be uploaded: {files}. You can try again from the ticket.',
    },
    coverage: {
      title: 'Who helps with what',
      supportHeading: 'Data Rodeo support',
      included: 'Included',
      supportBugs: 'Bugs and errors in Data Mission Sync. Bugs never use anyone\'s hours.',
      supportProduct: 'How the product and its features work.',
      supportKnowledgeBase: 'The knowledge base and video library, included with your subscription.',
      partnerTag: 'Your partner',
      partnerSetup: 'Your configuration and setup.',
      partnerProcesses: 'Your business processes and anything built around Data Mission Sync.',
      partnerServices: 'Services and project work. It\'s offered to your partner first.',
      contactPartner: 'Contact {name}',
      wranglerHeading: 'Data Wrangler services',
      paidHours: 'Paid hours',
      wranglerSetup: 'Your configuration, setup and processes around Data Mission Sync.',
      wranglerHandsOn: 'Hands-on help from a Data Wrangler, taken from your purchased hours. Data Wrangler hours are always paid.',
      knowledgeBase: 'Browse the knowledge base',
    },
    nextSteps: {
      title: 'What happens after you submit',
      aiSummary: 'We add an AI summary to the top of your ticket, often with articles that may help.',
      bugs: 'Possible bugs go straight to our development team.',
      partner: 'For your setup and configuration, {name} can help.',
      wrangler: 'For your setup and configuration, a Data Wrangler can help using your organization\'s hours.',
      waiting: 'When we need something from you, the ticket shows Waiting on you.',
    },
    hours: {
      title: 'Data Wrangler hours',
      scope: 'For your whole organization. Time spent on a ticket is logged in its conversation.',
      available: 'Available',
      spent: 'Used to date',
      assigned: 'Total purchased',
      value: '{hours} h',
      buy: 'Buy hours',
      askAdmin: 'Ask an administrator to buy hours.',
      loadFailed: 'Hours couldn\'t be loaded.',
    },
    buyHours: {
      title: 'Buy Data Wrangler hours',
      description: 'Choose a package. You\'ll finish the purchase on a secure Stripe page, and the hours are added to your organization once payment completes.',
      loading: 'Loading packages...',
      loadFailed: 'Packages couldn\'t be loaded.',
      empty: 'No hour packages are available right now.',
      packageHours: '{hours} hours',
      checkout: 'Continue to payment',
      redirecting: 'Opening payment...',
      checkoutFailed: 'The payment page couldn\'t be opened.',
      cancel: 'Cancel',
    },
    detail: {
      back: 'Back to Support',
      loadFailed: 'Failed to load the support ticket',
      openedBy: 'Opened by',
      created: 'Created',
      updated: 'Last updated',
      resolved: 'Resolved',
      detailsTitle: 'Details',
      statusLabel: 'Status',
      type: 'Type of help',
      resolutionCategory: 'Resolution category',
      notCategorized: 'Not categorized yet',
      descriptionTitle: 'Description',
      attachmentsTitle: 'Attachments',
      noAttachments: 'No files attached.',
      addFiles: 'Add files',
      uploading: 'Uploading...',
      uploaded: 'File uploaded',
      uploadFailed: 'Failed to upload {name}',
      uploadedBy: 'Uploaded by {name}',
      download: 'Download',
      downloadFailed: 'Failed to download the file',
      scanning: 'Scanning',
      scanningTooltip: 'The file is being checked for viruses and will be available shortly.',
      unavailable: 'Unavailable',
      unavailableTooltip: 'The file failed the virus check and cannot be downloaded.',
      conversationTitle: 'Conversation',
      replyCount: '{count} replies · newest first',
      noComments: 'No comments yet.',
      yourTurn: 'Your turn',
      yourTurnBody: 'Support is waiting on your reply to keep this ticket moving.',
      commentLabel: 'Reply to support',
      commentPlaceholder: 'Write a reply to the support team',
      commentRequired: 'Comment is required',
      commentTooLong: 'Comment must be {max} characters or fewer',
      attachToReply: 'Attach files',
      postComment: 'Send reply',
      posting: 'Posting...',
      commentFailed: 'Failed to post the comment',
      showMore: 'Show full reply',
      showLess: 'Show less',
      openImage: 'Open {name} full size',
      imageViewerTitle: 'Attachment preview',
      previousImage: 'Previous image',
      nextImage: 'Next image',
      imageCount: '{index} of {total}',
    },
    attachmentErrors: {
      tooLarge: '{name} is larger than {size}.',
      typeNotAllowed: '{name} is not an allowed file type.',
      nameTooLong: '{name} has a name longer than {max} characters.',
    },
  },
  runs: {
    runStates: {
      Completed: 'Completed',
      CompletedTooltip:
        'Sync completed successfully. All data has been extracted from the source and loaded into the target database.',
      Failed: 'Failed',
      FailedTooltip:
        'Sync encountered an error and could not complete. Click the row to view detailed error information.',
      Extracting: 'Extracting',
      ExtractingTooltip: 'Data is currently being extracted from the source table.',
      Loading: 'Loading',
      LoadingTooltip: 'Data is currently being loaded into the target database.',
      WaitingForTargetLease: 'Waiting for Target',
      WaitingForTargetLeaseTooltip:
        'Waiting to acquire a lock on the target database before loading can begin.',
      WaitingForSourceCapacity: 'Waiting for Source Capacity',
      WaitingForSourceCapacityTooltip:
        'This Dataverse environment is at its limit of concurrent requests from Data Mission Sync. This table will start as soon as capacity frees up.',
      Scheduled: 'Scheduled',
      ScheduledTooltip:
        'Run is waiting in the queue to execute, possibly because a previous sync on the source table is still in progress.',
      Skipped: 'Skipped',
      SkippedTooltip:
        'Sync was skipped and did not execute. Click the row to see the reason why it was skipped.',
      Cancelling: 'Cancelling',
      CancellingTooltip: 'A cancellation request has been sent. The sync will stop at the next checkpoint.',
      Cancelled: 'Cancelled',
      CancelledTooltip: 'Sync was manually cancelled or stopped before it could complete.',
      Unknown: 'Unknown',
      UnknownTooltip: 'The sync state is unknown or could not be determined.',
    },
    skipReasons: {
      NoChangesDetected: 'No changes detected since the last sync',
      InsufficientEntitlements: 'Insufficient entitlements to run this sync',
      ObsoleteCheckpoint: 'The checkpoint is obsolete and a reset is required',
      InitialSyncAlreadyCompleted: 'Initial sync has already been completed for this table',
      EntityNotAvailable:
        'This entity is reported in metadata but is not queryable in this environment',
    },
    syncMode: {
      Incremental: 'Incremental',
      Full: 'Full',
      ResetAndFull: 'Reset & Full',
    },
    triggerSource: {
      Manual: 'Manual',
      Scheduled: 'Scheduled',
    },
    errorDialog: {
      title: 'Run Error Details',
      runId: 'Run ID',
      sourceTable: 'Source Table',
      errorMessage: 'Error Message',
      errorDetail: 'Details',
      externalErrorCode: 'External Error Code',
      externalErrorMessage: 'External Error Message',
      close: 'Close',
      unknown: 'Unknown',
    },
    skipReasonDialog: {
      title: 'Run Skip Reason',
      runId: 'Run ID',
      sourceTable: 'Source Table',
      reason: 'Reason',
      close: 'Close',
      unknown: 'Unknown',
    },
  },
  sidebar: {
    navigation: 'Navigation',
    dashboard: 'Dashboard',
    profiles: 'Profiles',
    allRuns: 'All Runs',
    organizationMembers: 'Organization Members',
    agents: 'Agents',
    billing: 'Billing',
    settings: 'Settings',
    help: 'Help',
    support: 'Support',
    administration: 'Administration',
    sync: 'Sync',
    organizationSwitcher: {
      noOrganizations: 'No Organizations',
      organization: 'Organization',
      organizationSingular: 'organization',
      organizationPlural: 'organizations',
      newOrganization: 'New Organization',
      pendingInvitations: 'Pending invitations',
      acceptInvitation: 'Accept invitation',
    },
    userMenu: {
      settings: 'Settings',
      signOut: 'Sign Out',
    },
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Welcome to DataMission',
    profileActivity: 'Profile Activity (Last 24 Hours)',
    createNewProfile: 'Create New Profile',
    gettingStarted: {
      title: 'Getting Started',
      description: 'Create your first profile to start synchronizing data',
      createButton: 'Create Your First Profile',
    },
    alerts: {
      title: 'Alerts',
      loading: 'Loading...',
      incompleteProfile: 'Incomplete Profile Setup',
      profileMissing: 'Profile',
      isMissing: 'is missing:',
      sourceConfiguration: 'Source configuration',
      targetConfiguration: 'Target configuration',
      completeSetup: 'Complete Setup',
    },
    entitlements: {
      title: 'Organization Overview',
      loading: 'Loading...',
      profiles: 'Profiles',
      activeAvailable: 'Active / Available',
      freeTrial: 'Free Trial',
      active: 'Active',
      inactive: 'Inactive',
      expires: 'Expires',
      notAvailable: 'N/A',
      noActiveSubscription: 'No active subscription or free trial',
      contactSupport: 'Please contact support to activate your subscription',
    },
    runSummary: {
      last24Hours: 'Last 24 hours',
      table: 'table',
      tables: 'tables',
      noTablesConfigured: 'No tables configured',
      sourceTables: 'Source Tables:',
      lastSync: 'Last sync:',
      nextSync: 'Next sync:',
      noRecentSyncs: 'No recent syncs',
      justNow: 'Just now',
      minuteAgo: 'minute ago',
      minutesAgo: 'minutes ago',
      hourAgo: 'hour ago',
      hoursAgo: 'hours ago',
      dayAgo: 'day ago',
      daysAgo: 'days ago',
      totalRuns: 'Total Runs',
      successful: 'Successful',
      failed: 'Failed',
      inProgress: 'In Progress',
      skipped: 'Skipped',
      successRate: 'Success Rate',
    },
  },
  profiles: {
    title: 'Profiles',
    description: 'Manage your data synchronization profiles',
    createProfile: 'Create Profile',
    limitReached: 'You have reached the maximum number of profiles',
    noPermission: 'You do not have permission to create profiles in this organization',
    noProfiles: 'No profiles found. Create your first profile to get started.',
    table: {
      name: 'Name',
      region: 'Region',
      lastSync: 'Last Sync',
      nextSync: 'Next Sync',
      tables: 'Tables',
      status: 'Status',
      quietTime: 'Quiet Time',
      edit: 'Edit',
      burstActive: 'Burst active',
    },
    status: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    sync: {
      noRecentSyncs: 'No recent syncs',
      justNow: 'Just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago',
      daysAgo: 'd ago',
      notScheduledDisabled: 'Not scheduled (profile disabled)',
    },
    tables: {
      noTables: 'No tables configured',
      sourceTables: 'Source Tables:',
    },
    quietTime: {
      none: 'None',
      schedule: 'schedule',
      schedules: 'schedules',
    },
    sourceTables: {
      title: 'Source Tables',
      description: 'Tables configured for synchronization from this source',
      logicalName: 'Logical Name',
      status: 'Status',
      initialSyncCompleted: 'Initial Sync Completed',
      actions: 'Actions',
      yes: 'Yes',
      no: 'No',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enabledLabel: 'Enabled',
      autoDisabled: 'Auto-Disabled',
      autoDisabledTitle: 'Auto-Disabled Tables',
      autoDisabledDescription:
        'One or more tables were automatically disabled due to persistent failures. Please review the run history in the section below to identify and resolve the underlying issues before re-enabling.',
      autoDisabledTooltip: 'Table was automatically disabled due to persistent failures',
      notSupported: 'Not Supported',
      notSupportedTitle: 'Unsupported Tables',
      notSupportedDescription:
        'One or more tables are not currently supported by the sync engine. Please contact support for assistance.',
      notSupportedTooltip: 'This entity is not supported by the sync engine',
      changeTrackingRequired: 'Change Tracking Required',
      changeTrackingRequiredTitle: 'Change Tracking Required',
      changeTrackingRequiredDescription:
        'One or more tables were automatically disabled because the source cannot provide their changes until change tracking is enabled. For a relationship (many-to-many) table, change tracking must be enabled on both related tables. Enable it in the source system, then re-enable the table.',
      changeTrackingRequiredTooltip: 'Enable change tracking in the source (on both related tables for a relationship table), then re-enable this table',
      initialSync: 'Initial Sync',
      incrementalSync: 'Incremental Sync',
      inventorySync: 'Sync Inventory',
      syncMode: 'Sync Mode',
      syncModeIncremental: 'Incremental',
      syncModeIncrementalTooltip:
        'Dataverse change tracking delivers only changed rows since the last sync.',
      syncModeIncrementalOnTooltip:
        'Only rows with a newer {field} are extracted on each sync.',
      viaFieldLabel: 'via {field}',
      syncModeFull: 'Full',
      syncModeFullTooltip:
        'No watermark field is available, so every sync re-extracts the entire table.',
      deletions: 'Delete Detection',
      deletionsViaField: 'Column',
      deletionsViaFieldTooltip:
        "Deleted rows are detected using the '{field}' column on every sync.",
      deletionsViaInventory: 'Inventory',
      deletionsViaInventoryNextDate: 'on {date}',
      deletionsViaInventoryPendingInitialSync: 'pending initial sync',
      deletionsViaInventoryTooltip:
        'No deletion column exists on this entity, so a periodic inventory sync reconciles deletes by comparing primary keys.',
      deletionsPending: 'Pending detection',
      deletionsPendingTooltip:
        'Deletion strategy will be determined after the first sync.',
      deletionsViaChangeTracking: 'Change tracking',
      deletionsViaChangeTrackingTooltip:
        'Dataverse change tracking emits delete events natively.',
      delete: 'Delete',
      noTablesConfigured: 'No source tables configured yet.',
      noSearchResults: 'No tables match your search.',
      resetAndResync: 'Reset and Resync',
      viewTargetSchema: 'View Target Schema',
      setParent: 'Set parent table',
      childOf: 'Child of {name}',
      parentDialogTitle: 'Set parent table',
      parentDialogDescription:
        'Gate this table behind another so it only syncs once its parent has finished. Choose None to run it independently.',
      parentLabel: 'Parent table',
      parentNone: 'None (runs independently)',
      parentUpdateSuccess: 'Parent table updated',
      deleteTitle: 'Delete Source Table',
      deleteDescription: 'Deleting a source table stops future syncs.',
      deleteWarning: 'This deletes the source table and will stop all future syncs.',
      confirmationLabel: 'table name',
      successDeleted: 'Source table deleted successfully',
      successReset: 'Drop and reset initiated successfully',
      syncTriggeredSuccessfully: 'Sync triggered successfully',
      tableEnabledSuccessfully: 'Source table enabled successfully',
      tableDisabledSuccessfully: 'Source table disabled successfully',
      tableDisabledSyncTooltip: 'Enable the table to initiate a sync',
      initialSyncInProgressTooltip: 'The initial sync for this table is already queued or running',
    },
    targetSchemaDialog: {
      title: 'Target Schema',
      availableAfterFirstSync: 'The target schema is available after the first sync completes.',
      loadError: 'Failed to load the target schema. Please try again.',
      tooLargeError: 'This table\'s schema is too large to display.',
      columnName: 'Column',
      sqlType: 'SQL Type',
      nullable: 'Nullable',
      primaryKey: 'Primary Key',
      primaryKeyBadge: 'PK',
      close: 'Close',
    },
    sourceCard: {
      title: 'Source',
      description: 'Data source for this profile',
      testing: 'Testing...',
      testConnection: 'Test Connection',
      delete: 'Delete',
      noSourceAlert:
        'A source must be added to this profile to synchronize data. Use "Resume Setup" to configure a Dataverse source.',
      type: 'Type',
      unknown: 'Unknown',
      baseUrl: 'Base URL',
      copyBaseUrl: 'Copy Base URL',
      notAvailable: 'N/A',
      created: 'Created',
      connectionStatus: 'Connection Status',
      success: 'Success',
      lastTest: 'Last Test',
      neverTested: 'Never tested',
      connectionSuccessful: 'Connection successful',
      testSuccessful: 'Connection test successful',
      testFailed: 'Connection test failed',
      copiedToClipboard: 'Copied to clipboard',
      failedToCopy: 'Failed to copy',
      restApiUsage: 'REST API Usage',
      restApiUsageLastChecked: 'Last checked',
      restApiUsageNotAvailable: 'Not available',
      bulkApi: 'Bulk API',
      bulkApiAvailable: 'Available',
      bulkApiUnavailable: 'Unavailable',
    },
    targetCard: {
      title: 'Target',
      description: 'Data target for this profile',
      testing: 'Testing...',
      testConnection: 'Test Connection',
      delete: 'Delete',
      noTargetAlert:
        'A target must be added to this profile to synchronize data. Use "Resume Setup" to configure an Azure SQL target.',
      type: 'Type',
      unknown: 'Unknown',
      server: 'Server',
      copyServer: 'Copy Server',
      database: 'Database',
      copyDatabase: 'Copy Database',
      schema: 'Schema',
      copySchema: 'Copy Schema',
      tablePrefix: 'Table Prefix',
      noTablePrefix: 'None',
      notAvailable: 'N/A',
      created: 'Created',
      connectionStatus: 'Connection Status',
      success: 'Success',
      lastConnectionTest: 'Last Connection Test',
      neverTested: 'Never tested',
      nextSync: 'Next Sync',
      connectionSuccessful: 'Connection successful',
      testSuccessful: 'Connection test successful',
      testFailed: 'Connection test failed',
      copiedToClipboard: 'Copied to clipboard',
      failedToCopy: 'Failed to copy',
    },
    editDialog: {
      buttonText: 'Edit Profile',
      title: 'Edit Profile',
      description: 'Update profile information',
      name: 'Name',
      namePlaceholder: 'My Profile',
      region: 'Region',
      regionPlaceholder: 'eastus',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save Changes',
      updateSuccess: 'Profile updated successfully',
      dataverseLengthsTitle: 'Dataverse column lengths',
      dataverseLengthsDescription:
        'Maximum lengths for Dataverse label and multi-select columns. Changes apply the next time each table is fully reset and re-synced.',
      lookupLabelLength: 'Lookup label',
      choiceLabelLength: 'Choice label',
      multiSelectLength: 'Multi-select',
      lengthReduceWarning:
        'Reducing a length may truncate existing values on the next reset. Recoverable by raising it and re-syncing.',
    },
    syncSettings: {
      buttonText: 'Sync Settings',
      title: 'Sync Settings',
      description: 'Configure synchronization behavior for this profile.',
      deleteModeLabel: 'Delete Mode',
      warningTitle: 'Warning: This may cause data loss',
      warningDescription:
        'Changing the delete mode after initial synchronization may result in data inconsistencies or loss. Records that were previously soft-deleted may be permanently removed, or hard-deleted records may not be properly tracked.',
      acknowledgeChange: 'I understand this may cause data loss and want to proceed',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      updateSuccess: 'Sync settings updated successfully',
    },
  },
  syncButton: {
    sync: 'Sync',
    syncing: 'Syncing...',
    incrementalSync: 'Incremental Sync',
    resetAndFullSync: 'Reset + Full Sync',
  },
  profileDetail: {
    outboundIp: 'Outbound IP',
    status: {
      active: 'Active',
      paused: 'Paused',
    },
    actions: {
      copyProfileId: 'Copy Profile ID',
      help: 'Help',
      updating: 'Updating...',
      disable: 'Disable',
      enable: 'Enable',
      enabled: 'Enabled',
      syncing: 'Syncing...',
      sync: 'Sync',
      resetFullSync: 'Reset + Full Sync',
      triggerInitialSync: 'Trigger Initial Sync',
      resumeSetup: 'Resume Setup',
      editProfile: 'Edit Profile',
      deleteProfile: 'Delete Profile',
      actions: 'Actions',
      syncDisabledTooltip: 'Enable the profile to initiate a sync',
    },
    quietTime: {
      title: 'Quiet Time Periods',
      description: 'Syncs will be skipped during these time windows',
      addQuietTimeBlock: 'Add Quiet Time Block',
      loadingSchedules: 'Loading schedules...',
      noSchedules: 'No quiet time schedules configured',
      recurrence: 'Recurrence',
      startTime: 'Start Time',
      endTime: 'End Time',
      daily: 'Daily',
      noDaysSelected: 'No days selected',
    },
    createScheduleDialog: {
      title: 'Add New Quiet Time Block',
      description: 'All times are in UTC. Syncs will be skipped during these time windows.',
      type: 'Type',
      everyDay: 'Every Day',
      specificDays: 'Specific Days',
      selectDays: 'Select Days',
      startTime: 'Start Time (UTC)',
      endTime: 'End Time (UTC)',
      hourPlaceholder: 'HH',
      minutePlaceholder: 'MM',
      durationError: 'Quiet time must not exceed 8 hours.',
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Schedule',
      createSuccess: 'Schedule created successfully',
      createError: 'Failed to create schedule',
    },
    burst: {
      menuItem: 'Sync burst',
      title: 'Sync burst',
      description:
        'Temporarily run this profile on an accelerated sync cadence. Quiet-time windows are still respected.',
      windowLabel: 'Burst window (hours)',
      windowHelp:
        'Runs on a 5-minute accelerated sync for up to {max} hours, then returns to the normal schedule.',
      activeUntil: 'Burst active until {time}.',
      activeBadge: 'Burst active',
      overlapWarning:
        'This burst window overlaps a quiet-time period; syncs are still skipped during quiet time.',
      activate: 'Activate burst',
      stop: 'Stop burst',
      cancel: 'Cancel',
      activateSuccess: 'Burst activated',
      activateError: 'Failed to activate burst',
      stopSuccess: 'Burst stopped',
      stopError: 'Failed to stop burst',
    },
    daysOfWeek: {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
    },
    runHistory: {
      title: 'Run History',
      description: 'View sync runs for this profile',
      recentRuns: 'Recent Runs',
    },
    errors: {
      failedToLoad: 'Failed to load profile',
      copiedToClipboard: 'Copied to clipboard',
      failedToCopy: 'Failed to copy',
      profileEnabled: 'Profile enabled successfully',
      profileDisabled: 'Profile disabled successfully',
      syncTriggered: 'Sync triggered successfully',
      failedToLoadRuns: 'Failed to load runs',
    },
    helpContent: {
      profileHelp: 'Profile Help',
      profileHelpDescription: 'Learn about managing profiles, syncs, and run history',
      setupChecklistTitle: 'Profile Setup Checklist',
      setupChecklistDescription: 'Track your profile setup progress',
      checklistItems: {
        configureSource: 'Configure source',
        configureTarget: 'Configure target',
        selectTables: 'Select tables to sync',
        configureQuietTime: 'Configure your quiet time',
        configureDeleteMode: 'Configure delete mode',
        runInitialSync: 'Run initial sync',
      },
      allStepsCompleted: '✓ All setup steps completed! Your profile is ready to sync.',
      profileStatus: {
        title: 'Profile Status',
        active: 'Active:',
        activeDescription: 'Profile is enabled and can run scheduled syncs',
        paused: 'Paused:',
        pausedDescription: 'Profile is disabled and syncs will not run',
      },
      syncActions: {
        title: 'Sync Actions',
        sync: 'Sync:',
        syncDescription: 'Performs an incremental sync, only processing new or changed data',
        resetFullSync: 'Reset + Full Sync:',
        resetFullSyncDescription:
          'Deletes all existing data and performs a complete sync from the source. This is a destructive operation.',
        triggerInitialSync: 'Trigger Initial Sync:',
        triggerInitialSyncDescription:
          'Runs the first sync after setting up your source and target',
      },
      sourceAndTarget: {
        title: 'Source & Target',
        source: 'Source:',
        sourceDescription: 'The data source where your data originates',
        target: 'Target:',
        targetDescription: 'The destination where synced data will be stored',
        configurationNote: 'Both source and target must be configured before you can trigger syncs',
      },
      quietTimePeriods: {
        title: 'Quiet Time Periods',
        description:
          'Configure time windows when syncs will be automatically skipped. This is useful for avoiding syncs during maintenance windows or peak usage times.',
      },
      runHistory: {
        title: 'Run History',
        viewAllRuns:
          'View all sync runs for this profile, including their status, duration, and results',
        filterRuns: 'Filter runs by state (Completed, Failed, etc.) or by source table',
        clickFailedRuns: 'Click on failed runs to view detailed error information',
        usePagination: 'Use pagination controls to navigate through historical runs',
      },
      runStates: {
        title: 'Run States',
        completed: 'Completed:',
        completedDescription: 'Sync finished successfully',
        failed: 'Failed:',
        failedDescription: 'Sync encountered an error',
        extractingLoading: 'Extracting/Loading:',
        extractingLoadingDescription: 'Sync is in progress',
        scheduled: 'Scheduled:',
        scheduledDescription: 'Sync is queued to run',
        skipped: 'Skipped:',
        skippedDescription: 'Sync was skipped (e.g., due to quiet time period)',
        cancelled: 'Cancelled:',
        cancelledDescription: 'Sync was cancelled',
      },
    },
  },
  settings: {
    title: 'Settings',
    description: 'Manage your account settings and preferences',
    profileInfo: {
      title: 'Profile Information',
      description:
        'Update your personal information. This will be displayed across the application.',
    },
    fields: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      emailNote: 'Email cannot be changed',
    },
    actions: {
      save: 'Save Changes',
      saving: 'Saving...',
    },
    language: {
      label: 'Language',
      description: 'Select your preferred language for the application interface.',
      english: 'English',
      french: 'French',
      german: 'German',
      spanish: 'Spanish',
    },
    appearance: {
      title: 'Appearance',
      description: 'Customize the application theme.',
      themeLabel: 'Theme',
      themeDescription: 'Choose light, dark, or system mode.',
      toggleTheme: 'Toggle theme',
      themeOptions: {
        light: 'Light',
        dark: 'Dark',
        system: 'System',
      },
    },
    notifications: {
      updateSuccess: 'Profile updated successfully',
      updateError: 'Failed to update profile. Please try again.',
    },
    emailNotifications: {
      title: 'Email Notifications',
      description: 'Choose which email notifications you would like to receive.',
      save: 'Save Preferences',
      saving: 'Saving...',
      updateSuccess: 'Notification preferences updated successfully',
      updateError: 'Failed to update notification preferences. Please try again.',
      sections: {
        general: 'General',
        billing: 'Billing',
        dataMission: 'Data Mission',
        marketing: 'Marketing',
      },
      events: {
        UserInvited: {
          label: 'Organization Invitations',
          description: 'Receive an email when you are invited to join an organization.',
        },
        TrialConsumed: {
          label: 'Trial Activated',
          description:
            'Receive an email when a product trial is activated, including trial duration and end date.',
        },
        PaymentFailed: {
          label: 'Payment Failed',
          description: 'Receive an email when a payment attempt fails.',
        },
        BillingReminder: {
          label: 'Billing Reminders',
          description: 'Receive reminders about upcoming billing.',
        },
        PaymentMethodChanged: {
          label: 'Payment Method Updated',
          description: 'Receive an email when your payment method is updated.',
        },
        SubscriptionCanceled: {
          label: 'Subscription Canceled',
          description: 'Receive an email when a subscription is canceled.',
        },
        SubscriptionSuspended: {
          label: 'Subscription Suspended',
          description: 'Receive an email when a subscription is suspended due to payment issues.',
        },
        DataMissionSyncFailed: {
          label: 'Sync Failures',
          description:
            'Receive an email when a source table fails to sync successfully in Data Mission.',
        },
        ProductUpdates: {
          label: 'Product Updates, Features & Services',
          description: 'Stay informed about new features, product updates, and service enhancements.',
        },
        NewsTrainingArticles: {
          label: 'News, Training and Articles',
          description: 'We\'ll send you emails with news, training updates, and helpful articles about Data Mission Sync.',
        },
      },
    },
    orgSettings: {
      title: 'Organization Settings',
      description: 'Configure organization-level preferences and defaults',
      organizationName: 'Organization Name',
      organizationNameDescription: 'The display name for this organization',
      organizationNameRequired: 'Organization name is required',
      billingCountry: 'Billing Country',
      billingCountryDescriptionSet: 'The billing country for this organization (cannot be changed)',
      billingCountryDescriptionNotSet: 'Select the billing country for this organization (cannot be changed once set)',
      defaultRegion: 'Default Region',
      defaultRegionDescription: 'The default Azure region for new profiles in this organization',
      noDefaultRegion: 'No default region',
      save: 'Save Organization Settings',
      saving: 'Saving...',
      updateSuccess: 'Organization settings updated successfully',
      updateError: 'Failed to update organization settings',
      noOrganizationSelected: 'No organization selected',
    },
    featureFlags: {
      title: 'Feature Flags',
      description: 'Enable or disable features for this organization.',
    },
    legalDocuments: {
      title: 'Legal Documents',
      description: 'Review your acceptance status for our legal documents.',
      privacyPolicy: 'Privacy Policy',
      termsOfUse: 'Terms of Use',
      currentVersion: 'Current version',
      acceptedOn: 'Accepted on',
      neverAccepted: 'Not yet accepted',
      view: 'View',
      close: 'Close',
      version: 'Version',
    },
  },
  targets: {
    createDialog: {
      title: 'Create Azure SQL Target',
      description: 'Add a new Azure SQL target database to this profile',
      addTarget: 'Add Target',
      onlyOneTargetTooltip:
        'Only one target per profile. Delete the existing target to create a new one.',
      targetType: 'Target Type',
      targetTypeDescription: 'Currently only Azure SQL Database targets are supported',
      authType: 'Authentication Type',
      authTypeServicePrincipal: 'Service Principal (AAD)',
      authTypeServicePrincipalDescription: 'Authenticate using Azure AD service principal credentials',
      authTypeSqlCredential: 'SQL Credentials',
      authTypeSqlCredentialDescription: 'Authenticate using a SQL username and password',
      useSourceClientId: 'Use source client ID',
      useSourceClientIdDescription:
        "Copy the client ID from the source. You'll still need to provide the tenant ID and client secret.",
      usingClientIdFromSource: 'Using client ID from source',
      tenantId: 'Tenant ID',
      tenantIdPlaceholder: '00000000-0000-0000-0000-000000000000',
      tenantIdDescription: 'The Azure AD tenant ID',
      clientId: 'Client ID',
      clientIdPlaceholder: '00000000-0000-0000-0000-000000000000',
      clientIdDescription: 'The Azure AD application (client) ID',
      clientSecret: 'Client Secret',
      clientSecretPlaceholder: 'Enter client secret',
      clientSecretDescription: 'The Azure AD application client secret',
      username: 'Username',
      usernamePlaceholder: 'Enter SQL username',
      usernameDescription: 'The SQL Server login username',
      password: 'Password',
      passwordPlaceholder: 'Enter SQL password',
      passwordDescription: 'The SQL Server login password',
      encryptConnection: 'Encrypt connection',
      encryptConnectionDescription: 'Disable if connecting to a local SQL Server without TLS configured',
      server: 'Server',
      serverPlaceholder: 'myserver.database.windows.net',
      serverDescription: 'Azure SQL server name (e.g., myserver.database.windows.net)',
      database: 'Database',
      databasePlaceholder: 'MyDatabase',
      databaseDescription: 'Name of the target database',
      schema: 'Schema',
      schemaPlaceholder: 'dbo',
      schemaDescription: "SQL schema name (defaults to 'dbo')",
      agent: 'Agent',
      agentPlaceholder: 'Select an agent',
      agentDescription: 'Optionally assign a self-hosted agent to run sync operations for this target',
      agentNone: 'None (cloud-hosted)',
      connectionTest: 'Connection Test',
      testConnection: 'Test Connection',
      testing: 'Testing...',
      testDescription: 'Test your connection before creating the target',
      connectionSuccessful: 'Connection successful',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      saveAndCreateSource: 'Save and Create Source',
      allFieldsRequired: 'All fields are required to test the connection',
      validationFailed: 'Validation failed. Please check the form.',
    },
    editDialog: {
      buttonText: 'Edit Target',
      title: 'Edit Target',
      description: 'Update the target configuration',
      tenantId: 'Tenant ID',
      tenantIdPlaceholder: '00000000-0000-0000-0000-000000000000',
      tenantIdDescription: 'The Azure AD tenant ID',
      clientId: 'Client ID',
      clientIdPlaceholder: '00000000-0000-0000-0000-000000000000',
      clientIdDescription: 'The Azure AD application (client) ID',
      clientSecret: 'Client Secret',
      clientSecretPlaceholder: 'Enter client secret',
      clientSecretDescription: 'The Azure AD application client secret',
      tenantIdRequired: 'Tenant ID is required',
      clientIdRequired: 'Client ID is required',
      clientSecretRequired: 'Client Secret is required',
      usernameRequired: 'Username is required',
      passwordRequired: 'Password is required',
      username: 'Username',
      usernamePlaceholder: 'Enter SQL username',
      usernameDescription: 'The SQL Server login username',
      password: 'Password',
      passwordPlaceholder: 'Enter SQL password',
      passwordDescription: 'The SQL Server login password',
      encryptConnection: 'Encrypt connection',
      encryptConnectionDescription: 'Disable if connecting to a local SQL Server without TLS configured',
      agent: 'Agent',
      agentPlaceholder: 'Select an agent',
      agentDescription: 'Assign a self-hosted agent to run sync operations for this target',
      agentNone: 'None (cloud-hosted)',
      testConnection: 'Test Connection',
      connectionSuccessful: 'Connection successful',
      cancel: 'Cancel',
      update: 'Update',
      updateSuccess: 'Target updated successfully',
    },
    prefixDialog: {
      buttonText: 'Change table prefix',
      title: 'Change table prefix',
      description: 'The table prefix is applied to every table this profile creates.',
      label: 'Table prefix',
      placeholder: 'e.g. dms_',
      hint: 'Letters, digits, and underscores only. Leave empty for no prefix.',
      invalidPrefix: 'Table prefix may only contain letters, digits, and underscores.',
      tablesTooLong: 'Some tables cannot use this prefix because their name would exceed the 128-character SQL limit:',
      warningTitle: 'This will drop and rebuild all synced tables',
      warningOperation1: 'All target tables (final and staging) for this profile are dropped.',
      warningOperation2: 'They are recreated with the new prefix and re-synced from scratch.',
      warningOperation3: 'Synced data is unavailable until the re-sync completes.',
      acknowledge: 'I understand all synced tables will be dropped and rebuilt.',
      cancel: 'Cancel',
      apply: 'Change prefix',
      updateSuccess: 'Table prefix updated',
    },
    notifications: {
      createSuccess: 'Target created successfully',
      createError: 'Failed to create target. Please try again.',
    },
  },
  sources: {
    pageSizeField: {
      label: 'Page size',
      description: 'Rows fetched per extraction request ({min}–{max}). Higher values mean fewer calls.',
    },
    createDialog: {
      title: 'Create Dataverse Source',
      description: 'Add a new Dataverse data source to this profile',
      addSource: 'Add Source',
      onlyOneSourceTooltip:
        'Only one source per profile. Delete the existing source to create a new one.',
      sourceType: 'Source Type',
      sourceTypeDescription: 'Currently only Microsoft Dataverse sources are supported',
      baseUrl: 'Base URL',
      baseUrlPlaceholder: 'https://org.crm.dynamics.com',
      baseUrlDescription: 'The HTTPS URL to your Dataverse API endpoint',
      clientId: 'Client ID',
      clientIdPlaceholder: '00000000-0000-0000-0000-000000000000',
      clientIdDescription: 'The Azure AD application (client) ID',
      clientSecret: 'Client Secret',
      clientSecretPlaceholder: 'Enter client secret',
      clientSecretDescription: 'The Azure AD application client secret',
      connectionTest: 'Connection Test',
      testConnection: 'Test Connection',
      testing: 'Testing Connection...',
      testDescription: 'Test your connection before creating the source',
      connectionSuccessful: 'Connection successful',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      saveAndCreateTarget: 'Save and Create Target',
      allFieldsRequired: 'All fields are required to test the connection',
      validationFailed: 'Validation failed. Please check the form.',
    },
    editDialog: {
      buttonText: 'Edit Source',
      title: 'Edit Source',
      description: 'Update the source configuration',
      baseUrl: 'Base URL',
      baseUrlDescription: 'The HTTPS URL to your Dataverse API endpoint',
      pageSize: 'Page Size',
      pageSizeDescription: 'Records fetched per Dataverse API call (1000–5000). Lower this if very wide tables time out; raise it to reduce API calls. Default 3000.',
      pageSizeRange: 'Page size must be between 1000 and 5000',
      clientId: 'Client ID',
      clientIdDescription: 'The Azure AD application (client) ID',
      clientSecret: 'Client Secret',
      clientSecretDescription: 'The Azure AD application client secret',
      consumerKey: 'Consumer Key',
      consumerKeyDescription: 'The Consumer Key from your Salesforce Connected App',
      secret: 'Secret',
      secretDescription: 'The Consumer Secret from your Salesforce Connected App',
      baseUrlRequired: 'Base URL is required',
      clientIdRequired: 'Client ID is required',
      clientSecretRequired: 'Client Secret is required',
      consumerKeyRequired: 'Consumer Key is required',
      secretRequired: 'Secret is required',
      instanceUrlRequired: 'Instance URL is required',
      tenantIdRequired: 'Tenant ID is required',
      environmentRequired: 'Environment is required',
      companyRequired: 'Please select a company after testing the connection',
      testConnection: 'Test Connection',
      connectionSuccessful: 'Connection successful',
      cancel: 'Cancel',
      updating: 'Updating...',
      update: 'Update Source',
      updateSuccess: 'Source updated successfully',
    },
    sourceTablesDialog: {
      buttonText: 'Add Source Table',
      title: 'Add Source Tables',
      description: 'Select one or more tables from this source to be synchronized',
      tables: 'Tables',
      selected: 'selected',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      searchPlaceholder: 'Search tables...',
      loading: 'Loading tables...',
      noTables: 'No tables found',
      selectAtLeastOne: 'Please select at least one table',
      cancel: 'Cancel',
      adding: 'Adding...',
      addTables: 'Add',
      addSuccess: 'Source tables added successfully!',
      timestampField: 'Choose a timestamp field for incremental syncs',
      timestampFieldDescription: 'Field used for incremental sync',
      timestampFieldNone: 'None (full replication)',
      incrementalSyncVia: 'Incremental sync via {field}',
      fullReplication: 'Full replication (no timestamp field available)',
      noTimestampFieldNotice: 'No timestamp columns are available for this table. Every sync will perform a full replication of all records.',
      nameTooLongForPrefix: "Name too long for this profile's table prefix.",
    },
    tableBatch: {
      progress: 'Adding {done} of {total}...',
      partialFailure: '{added} of {total} tables were added. {failed} could not be added:',
      moreTables: '(+{count} more)',
      syncStartFailed: '{count} tables were added, but their initial sync could not be started. Start a sync from the profile page.',
    },
    tablePaste: {
      open: 'Paste table list',
      description: 'Paste table logical names, one per line or separated by commas. Matching ignores upper and lower case.',
      placeholder: 'account\ncontact\nmsdyn_matchingcriteria',
      resolve: 'Select matching tables',
      cancel: 'Cancel',
      emptyInput: 'Paste at least one table name.',
      summary: "{selected} selected, {alreadySelected} already selected, {alreadyAdded} already added, {notSyncable} can't be synced, {notFound} not found.",
      columnTable: 'Table',
      columnResult: 'Result',
      columnDetails: 'Details',
      resultAlreadyAdded: 'Already added',
      alreadyAddedDetail: 'This table is already added to this profile.',
      resultNotSyncable: "Can't be synced",
      resultNotFound: 'Not found',
      didYouMean: 'Did you mean {name}?',
      noMatch: 'No table with this logical name exists in the source.',
      copyUnresolved: 'Copy unresolved names',
      copySelected: 'Copy selected',
      copied: 'Copied',
      dismiss: 'Dismiss',
    },
    sourceCapacity: {
      queuedTitle: 'Tables queued for Dataverse capacity',
      queuedDescription:
        '{count} tables are queued waiting for Dataverse capacity (limit: {limit} concurrent requests per environment). Syncs will complete, just more slowly.',
      manyTablesHint:
        'Syncs for this Dataverse environment run up to {limit} requests at a time. With {count} tables, large syncs will queue and take longer to complete.',
      selectionHint:
        'Syncs for this Dataverse environment run up to {limit} requests at a time. With {count} tables selected, the initial sync will queue and take longer to complete.',
      dedicatedAppNote:
        'Use an app registration dedicated to Data Mission Sync. Dataverse limits concurrent requests per app user, so sharing it with other integrations can cause throttling.',
    },
    notifications: {
      createSuccess: 'Source created successfully',
      createError: 'Failed to create source. Please try again.',
    },
  },
  wizard: {
    advancedSettings: {
      title: 'Advanced settings',
      description: 'Optional. Most customers can leave these as-is.',
    },
    steps: {
      profile: 'Profile',
      source: 'Source',
      target: 'Target',
      tables: 'Tables',
      quietTime: 'Quiet Time',
      deleteMode: 'Delete Mode',
      initialSync: 'Initial Sync',
    },
    availableTableDisabledReasons: {
      ChangeTrackingDisabled: 'Enable change tracking to use this entity.',
      NotSupported: 'This entity is not currently supported. Please contact support for assistance.',
      RelatedChangeTrackingDisabled: 'Enable change tracking on both related tables to use this relationship table.',
      VirtualTable: "This is a virtual table: its data isn't stored in Dataverse, so it can't be synced.",
    },
    common: {
      back: 'Back',
      next: 'Next',
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      remove: 'Remove',
      update: 'Update',
      submit: 'Submit',
      close: 'Close',
      copy: 'Copy',
      copied: 'Copied',
      copyToClipboard: 'Copied to clipboard',
      failedToCopy: 'Failed to copy',
    },
    profile: {
      title: 'Create Profile',
      titleEdit: 'Profile Details',
      description: 'Set up a new data synchronization profile',
      descriptionEdit: 'View your profile configuration',
      cardTitle: 'Profile Details',
      cardDescription: 'Configure the basic settings for your profile',
      profileName: 'Profile Name',
      profileNamePlaceholder: 'My Data Sync Profile',
      region: 'Region',
      regionPlaceholder: 'Select a region',
      creating: 'Creating...',
      nextConfigureSource: 'Next: Configure Source',
      profileCreatedSuccessfully: 'Profile created successfully',
      pleaseEnterProfileName: 'Please enter a profile name',
      advancedLengthsInvalid: 'Dataverse column lengths must be between 50 and 4000.',
    },
    source: {
      title: 'Configure Source',
      description: 'Connect to your data source',
      cardTitle: 'Connection Details',
      cardDescription: 'Enter your credentials and connection details',
      sourceType: 'Source Type',
      selectSourceType: 'Select a source type',
      dataverse: 'Dataverse',
      businessCentral: 'Business Central',
      salesforce: 'Salesforce',
      baseUrl: 'Base URL',
      baseUrlPlaceholder: 'https://yourorg.crm.dynamics.com',
      pageSize: 'Page Size',
      pageSizePlaceholder: '3000',
      pageSizeDescription: 'Records fetched per Dataverse API call (1000–5000). Lower this if very wide tables time out; raise it to reduce API calls. Default 3000.',
      pageSizeRange: 'Page size must be between 1000 and 5000',
      tenantId: 'Tenant ID',
      tenantIdPlaceholder: 'Enter Azure AD tenant ID',
      bcEnvironment: 'Environment',
      bcEnvironmentPlaceholder: 'e.g. production',
      instanceUrl: 'Instance URL',
      instanceUrlPlaceholder: 'https://mycompany.my.salesforce.com',
      isSandbox: 'Sandbox Org',
      isSandboxDescription: 'Enable if this is a Salesforce sandbox environment',
      clientId: 'Client ID',
      clientIdPlaceholder: 'Enter client ID',
      clientSecret: 'Client Secret',
      clientSecretPlaceholder: 'Enter client secret',
      consumerKey: 'Consumer Key',
      consumerKeyPlaceholder: 'Enter consumer key',
      secret: 'Secret',
      secretPlaceholder: 'Enter secret',
      testConnection: 'Test Connection',
      testConnectionAndLoadCompanies: 'Test Connection & Load Companies',
      testing: 'Testing Connection...',
      connectionSuccessful: 'Connection successful',
      company: 'Company',
      companyPlaceholder: 'Select a company',
      noCompaniesFound: 'No companies found',
      allFieldsRequired: 'All fields are required to test the connection',
      testBeforeProceeding: 'Please test the connection successfully before proceeding',
      selectCompanyBeforeProceeding: 'Please select a company before proceeding',
      back: 'Back',
      next: 'Next: Configure Target',
      creating: 'Creating...',
    },
    target: {
      title: 'Configure Target',
      description: 'Connect to your Azure SQL database',
      cardTitle: 'Azure SQL Connection',
      cardDescription: 'Enter your Azure SQL database credentials and details',
      warning: 'Warning: After the first sync, the target cannot be changed.',
      firewallTitle: 'Firewall Configuration Required',
      firewallDescription:
        'To allow Data Mission to connect to your Azure SQL server, choose one of these options:',
      firewallOptionAzure:
        'Enable "Allow Azure services and resources to access this server" in your Azure SQL server\'s networking settings.',
      firewallOptionAzureLabel: 'Option 2:',
      firewallOptionIpRecommended: 'Option 1 (Recommended):',
      firewallOptionIpDescription:
        'Add the following static IP address to your Azure SQL server firewall rules:',
      firewallIpUnavailable:
        'Static IP not available for this region. Use Option 1 instead.',
      firewallSummary:
        'Additional network configuration may be required to enable our services to access your Azure SQL server.',
      firewallLearnMore: 'Learn More',
      firewallClose: 'Close',
      authType: 'Authentication Type',
      authTypeServicePrincipal: 'Service Principal (AAD)',
      authTypeServicePrincipalDescription: 'Authenticate using Azure AD service principal credentials',
      authTypeSqlCredential: 'SQL Credentials',
      authTypeSqlCredentialDescription: 'Authenticate using a SQL username and password',
      useSourceClientId: 'Use source client ID',
      useSourceClientIdDescription:
        "Copy the client ID from the source. You'll still need to provide the tenant ID and client secret.",
      usingClientIdFromSource: 'Using client ID from source',
      tenantId: 'Tenant ID',
      tenantIdPlaceholder: 'Enter tenant ID',
      clientId: 'Client ID',
      clientIdPlaceholder: 'Enter client ID',
      clientSecret: 'Client Secret',
      clientSecretPlaceholder: 'Enter client secret',
      username: 'Username',
      usernamePlaceholder: 'Enter SQL username',
      usernameDescription: 'The SQL Server login username',
      password: 'Password',
      passwordPlaceholder: 'Enter SQL password',
      passwordDescription: 'The SQL Server login password',
      encryptConnection: 'Encrypt connection',
      encryptConnectionDescription: 'Disable if connecting to a local SQL Server without TLS configured',
      server: 'Server',
      serverPlaceholder: 'yourserver.database.windows.net',
      database: 'Database',
      databasePlaceholder: 'Enter database name',
      schema: 'Schema Name',
      schemaPlaceholder: 'dbo',
      tablePrefix: 'Table prefix (optional)',
      tablePrefixPlaceholder: 'e.g. dms_',
      tablePrefixDescription: 'Applied to every table this profile creates (e.g. dms_account). Include your own separator. Letters, digits, and underscores only.',
      tablePrefixInvalid: 'Table prefix may only contain letters, digits, and underscores.',
      agent: 'Agent',
      agentPlaceholder: 'Select an agent',
      agentDescription: 'Optionally assign a self-hosted agent to run sync operations for this target',
      agentNone: 'None (cloud-hosted)',
      testConnection: 'Test Connection',
      testing: 'Testing Connection...',
      connectionSuccessful: 'Connection successful',
      allFieldsRequired: 'All fields are required to test the connection',
      testBeforeProceeding: 'Please test the connection successfully before proceeding',
      validationFailed: 'Validation failed. Please check the form.',
      back: 'Back',
      next: 'Next: Select Tables',
      creating: 'Creating...',
    },
    tables: {
      title: 'Select Tables',
      description: 'Choose which tables to synchronize',
      cardTitle: 'Available Tables',
      cardDescription: 'Select one or more tables from your Dataverse source to be synchronized',
      tablesLabel: 'Tables',
      selected: 'selected',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      searchPlaceholder: 'Search tables by name...',
      loadingTables: 'Loading tables...',
      noTablesFound: 'No tables found matching your search',
      noTablesAvailable: 'No tables available',
      pleaseSelectAtLeastOne: 'Please select at least one table',
      sourceNotFound: 'Source not found',
      addingTables: 'Adding Tables...',
      finishAddTables: 'Finish & Add {count} Table',
      finishAddTablesPlural: 'Finish & Add {count} Tables',
      sourceTablesAddedSuccessfully: 'Source tables added successfully',
      nextConfigureQuietTime: 'Next: Configure Quiet Time',
      timestampField: 'Choose a timestamp field for incremental syncs',
      timestampFieldDescription: 'Field used for incremental sync',
      timestampFieldNone: 'None (full replication)',
      incrementalSyncVia: 'Incremental sync via {field}',
      fullReplication: 'Full replication (no timestamp field available)',
      noTimestampFieldNotice: 'No timestamp columns are available for this table. Every sync will perform a full replication of all records.',
      nameTooLongForPrefix: "Name too long for this profile's table prefix.",
      back: 'Back',
    },
    quietTime: {
      title: 'Configure Quiet Time',
      description: 'Set up time windows when syncs will be automatically skipped',
      cardTitle: 'Quiet Time Period',
      cardDescription:
        'Configure time windows when syncs will be skipped. This is optional but recommended.',
      infoAlert:
        'All times are in UTC. Quiet time periods help avoid syncs during maintenance windows or peak usage times.',
      existingSchedules:
        'You have {count} quiet time schedule configured. You can add more below or proceed to the next step.',
      existingSchedulesPlural:
        'You have {count} quiet time schedules configured. You can add more below or proceed to the next step.',
      type: 'Type',
      typeDaily: 'Every Day',
      typeWeekly: 'Specific Days',
      selectDays: 'Select Days',
      startTime: 'Start Time (UTC)',
      endTime: 'End Time (UTC)',
      hourPlaceholder: 'HH',
      minutePlaceholder: 'MM',
      durationExceeded: 'Quiet time must not exceed 8 hours. Current duration: {hours} hours',
      addQuietTimeBlock: 'Add Quiet Time Block',
      creating: 'Creating...',
      quietTimeScheduleCreatedSuccessfully: 'Quiet time schedule created successfully',
      back: 'Back',
      nextConfigureDeleteMode: 'Next: Configure Delete Mode',
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      configuredSchedules: 'Configured Schedules',
      recurrence: 'Recurrence',
      noDaysSelected: 'No days selected',
    },
    deleteMode: {
      title: 'Configure Delete Mode',
      description: 'Choose how deleted records are handled during synchronization',
      cardTitle: 'Delete Mode',
      cardDescription: 'Select how you want to handle deleted records from the source',
      softDelete: 'Soft Delete',
      softDeleteDescription:
        'Mark records as deleted (IsDeleted = true) but keep them in the database. Best for audit trails and historical data.',
      hardDelete: 'Hard Delete',
      hardDeleteDescription:
        'Physically remove records from the database when they are deleted in the source.',
      infoMessage:
        'This setting determines how records deleted in the source system are handled in the target database. Choose carefully as this affects data integrity.',
      recommended: 'Recommended',
      warningAfterInitialSync:
        'Warning: Changing the delete mode after initial sync may cause data inconsistencies. Proceed with caution.',
      saving: 'Saving...',
      nextRunInitialSync: 'Next: Run Initial Sync',
      successMessage: 'Delete mode configured successfully',
      back: 'Back',
    },
    initialSync: {
      title: 'Run Initial Sync',
      description: 'Trigger the first sync to start synchronizing your data',
      cardTitle: 'Initial Sync',
      cardDescription:
        'Run your first sync to start synchronizing data from your source to your target',
      infoAlert:
        'The initial sync will process all selected tables and create the initial data set in your target database. This may take some time depending on the amount of data.',
      triggeredSuccessfully: 'Initial sync triggered successfully! Redirecting to profile page...',
      triggering: 'Triggering initial sync...',
      triggeringButton: 'Triggering...',
      triggeredButton: 'Triggered',
      triggerInitialSync: 'Trigger Initial Sync',
      initialSyncTriggeredSuccessfully: 'Initial sync triggered successfully',
      back: 'Back',
    },
  },
  organizationSetupWizard: {
    title: 'Mission Data',
    createError: 'Failed to create organization',
    unexpectedError: 'An unexpected error occurred',
    regionSetupError: 'Failed to update organization settings',
    setupComplete: 'Organization setup complete',
  },
  organizationWizard: {
    welcome: {
      title: 'Welcome to Mission Impact Data Mission',
      description: "Let's get you set up with your organization",
      intro: "To get started, you'll need to create an organization. An organization helps you:",
      benefit1: 'Manage your team members and their access',
      benefit2: 'Organize your data sources and profiles',
      benefit3: 'Collaborate with others on data integration tasks',
      benefit4: 'Keep your work separate from other organizations',
      creatorNote:
        "As the creator, you'll automatically become the Administrator with full control over your organization.",
      getStarted: 'Get Started',
    },
    name: {
      title: 'Create Your Organization',
      description: 'Choose a name for your organization',
      organizationName: 'Organization Name',
      organizationNamePlaceholder: 'Enter organization name',
      organizationNameCannotBeEmpty: 'Organization name cannot be empty',
      creating: 'Creating...',
      createOrganization: 'Create Organization',
    },
    language: {
      title: 'Choose Your Language',
      description: 'Select your preferred language for the application',
      languageLabel: 'Language',
      languageDescription: 'You can change this later in settings',
      english: 'English',
      french: 'Français',
      german: 'Deutsch',
      spanish: 'Español',
      continue: 'Continue',
    },
    region: {
      title: 'Set Default Region',
      description: 'Choose a default Azure region for your organization',
      defaultRegion: 'Default Region',
      defaultRegionDescription: 'The default Azure region for new profiles in this organization',
      noDefaultRegion: 'No default region',
      saving: 'Saving...',
      complete: 'Complete Setup',
    },
    success: {
      title: 'Organization Created Successfully',
      description: 'Your organization is ready to use',
      message: "Your organization has been created and you've been assigned as the Administrator.",
      redirecting: 'Redirecting you to the dashboard...',
    },
  },
  userOnboardingWizard: {
    title: 'Welcome to Data Mission',
    steps: {
      welcome: 'Welcome',
      language: 'Language',
      theme: 'Theme',
      organization: 'Organization',
      eula: 'Agreement',
      complete: 'Complete',
    },
    welcomeStep: {
      title: 'Welcome to Data Mission',
      description: "Let's get you set up",
      intro:
        'Data Mission helps you synchronize your Microsoft Dataverse data to your own SQL Server database, giving you full control over your data for reporting and analysis.',
      feature1: 'Automated, incremental data synchronization',
      feature2: 'Full control over your synchronized data',
      feature3: 'Easy setup with guided configuration',
      getStarted: 'Get Started',
    },
    languageStep: {
      title: 'Choose Your Language',
      description: 'Select your preferred language for the application',
      languageLabel: 'Language',
      continue: 'Continue',
    },
    themeStep: {
      title: 'Choose Your Theme',
      description: 'Select your preferred color theme',
      light: 'Light',
      lightDescription: 'A bright theme for daytime use',
      dark: 'Dark',
      darkDescription: 'A darker theme that is easier on the eyes',
      system: 'System',
      systemDescription: 'Automatically match your system settings',
      back: 'Back',
      continue: 'Continue',
    },
    organizationStep: {
      title: 'Your Organizations',
      description:
        'Organizations help you manage your team and data. An organization is required to use Data Mission.',
      activeMemberships: 'Active Memberships',
      pendingInvites: 'Pending Invitations',
      pendingInvitesDescription: 'You have been invited to join the following organizations',
      role: 'Role',
      expires: 'Expires',
      accept: 'Accept',
      accepting: 'Accepting...',
      decline: 'Decline',
      declining: 'Declining...',
      createNew: 'Create New Organization',
      createNewDescription: 'Start fresh with your own organization',
      organizationName: 'Organization Name',
      organizationNamePlaceholder: 'Enter organization name',
      organizationNameRequired: 'Organization name is required',
      creating: 'Creating...',
      create: 'Create Organization',
      back: 'Back',
      acceptError: 'Failed to accept invitation. Please try again.',
      declineError: 'Failed to decline invitation. Please try again.',
      createError: 'Failed to create organization. Please try again.',
    },
    agreementStep: {
      title: 'Terms of Service',
      description: 'Please review and accept the terms to continue',
      loading: 'Loading...',
      loadError: 'Failed to load the agreement. Please try again later.',
      accept: 'Accept',
      accepting: 'Accepting...',
      back: 'Back',
      acceptError: 'Failed to accept the agreement. Please try again.',
      noAgreementsRequired: 'No agreements require acceptance at this time.',
      privacyPolicyTitle: 'Privacy Policy',
      termsOfUseTitle: 'Terms of Use',
      scrollToAccept: 'Scroll to the bottom to accept',
    },
    communicationPreferencesStep: {
      title: 'Choose your communication preferences',
      description: 'Select the types of communications you would like to receive from us',
      productUpdates: {
        label: 'Product Updates, Features & Services',
        description: 'Stay informed about new features, product updates, and service enhancements.',
      },
      newsTrainingArticles: {
        label: 'News, Training and Articles',
        description: 'We\'ll send you emails with news, training updates, and helpful articles about Data Mission Sync.',
      },
      back: 'Back',
      continue: 'Continue',
      saving: 'Saving...',
      saveError: 'Failed to save communication preferences. Please try again.',
    },
    completeStep: {
      title: 'Setup Complete',
      description: 'You are ready to start using Data Mission',
      summary: 'Your Preferences',
      language: 'Language',
      theme: 'Theme',
      completing: 'Completing...',
      complete: 'Get Started',
      error: 'Failed to complete setup. Please try again.',
    },
  },
  dialogs: {
    deleteProfile: {
      title: 'Delete Profile',
      description: 'Are you sure you want to delete the profile',
      confirmationLabel: 'profile name',
      buttonText: 'Delete Profile',
      successMessage: 'Profile deleted successfully',
      warningIntro: 'Deleting this profile will also permanently delete:',
      warningSources: 'All source(s) associated with this profile',
      warningTargets: 'All target(s) associated with this profile',
      warningSourceTables: 'All source tables associated with the sources',
      warningWebhooks: 'All webhook subscriptions associated with the sources',
    },
    deleteSource: {
      title: 'Delete Source',
      description: 'Are you sure you want to delete this source?',
      confirmationKey: 'DELETE SOURCE',
      confirmationLabel: 'confirmation text',
      buttonText: 'Delete',
      successMessage: 'Source deleted successfully',
      warningIntro: 'Deleting this source will also permanently delete:',
      warningRoutes: 'All routes that use this source',
      warningSourceTables: 'All source tables associated with this source',
      warningWebhooks: 'All webhook subscriptions associated with this source',
      baseUrl: 'Base URL:',
    },
    deleteTarget: {
      title: 'Delete Target',
      description: 'Are you sure you want to delete this target?',
      buttonText: 'Delete Target',
      cancel: 'Cancel',
      deleting: 'Deleting...',
      successMessage: 'Target deleted successfully',
      warning: 'Warning: This action cannot be undone.',
      warningDescription: 'Deleting this target will also permanently delete:',
      warningRoutes: 'All routes that use this target',
      server: 'Server:',
      database: 'Database:',
    },
    deleteSchedule: {
      title: 'Delete Schedule',
      description:
        'Are you sure you want to delete this quiet time schedule? This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete',
      successMessage: 'Schedule deleted successfully',
      errorMessage: 'Failed to delete schedule',
    },
    confirmDelete: {
      warning: 'WARNING:',
      defaultWarning: 'This action cannot be undone.',
      typeToConfirm: 'Type the following to confirm:',
      enterPrefix: 'Enter',
      cancel: 'Cancel',
      delete: 'Delete',
      deleting: 'Deleting...',
    },
    newOrganization: {
      title: 'Create New Organization',
      description: 'Enter a name for your new organization. You can change this later in settings.',
      organizationName: 'Organization Name',
      organizationNamePlaceholder: 'My Organization',
      billingCountry: 'Billing Country',
      billingCountryDescription: 'Select the country for billing purposes. This cannot be changed later.',
      billingCountryPlaceholder: 'Select a country',
      billingCountrySearch: 'Search countries...',
      billingCountryNoResults: 'No country found.',
      cancel: 'Cancel',
      create: 'Create',
      organizationCreatedSuccessfully: 'Organization created successfully!',
    },
    setBillingCountry: {
      title: 'Set Billing Country',
      description: 'Please select a billing country for your organization. This is required to continue.',
      billingCountry: 'Billing Country',
      billingCountryRequired: 'A billing country is required',
      save: 'Save',
      saving: 'Saving...',
      billingCountryUpdated: 'Billing country updated successfully!',
    },
    inviteUser: {
      title: 'Invite New User',
      description: 'Send an invitation to a new user to join your organization',
      email: 'Email',
      emailPlaceholder: 'john.doe@example.com',
      role: 'Role',
      rolePlaceholder: 'Select a role',
      roleReader: 'Reader',
      roleOperator: 'Operator',
      roleAdministrator: 'Administrator',
      roleBillingManager: 'Billing Manager',
      roleExpiration: 'Role Expiration Date (Optional)',
      roleExpirationPlaceholder: 'Pick a date',
      roleExpiresOn: "The user's role will expire on {date}",
      invitationExpiresNote:
        'Note: The invitation will automatically expire 7 days after it is sent',
      cancel: 'Cancel',
      sending: 'Sending...',
      sendInvite: 'Send Invite',
      userInvitedSuccessfully: 'User invited successfully!',
    },
    editUserRole: {
      title: 'Edit User Role',
      description: 'Change the role for {email}',
      roleLabel: 'Role',
      rolePlaceholder: 'Select a new role',
      membershipExpiration: 'Membership Expiration Date (Optional)',
      membershipExpirationIndefinite: 'Indefinite',
      membershipExpiresOn: 'Membership will expire on {date}',
      clearExpiration: 'Clear',
      cancel: 'Cancel',
      update: 'Update Role',
      updating: 'Updating...',
      triggerLabel: 'Edit user role',
      roleUpdated: 'Role updated successfully',
    },
    removeMember: {
      title: 'Remove User',
      description: 'Are you sure you want to remove {email} from this organization?',
      cancel: 'Cancel',
      remove: 'Remove User',
      removing: 'Removing...',
      triggerLabel: 'Remove user from organization',
      userRemoved: 'User removed from organization',
    },
    reinviteUser: {
      title: 'Re-invite User',
      description: 'Send a new invitation to {email}',
      emailLabel: 'Email',
      roleLabel: 'Role',
      rolePlaceholder: 'Select a role',
      membershipExpiration: 'Membership Expiration Date (Optional)',
      membershipExpirationIndefinite: 'Indefinite',
      membershipExpiresOn: 'Membership will expire on {date}',
      clearExpiration: 'Clear',
      invitationExpiresNote:
        'Note: The invitation will automatically expire 7 days after it is sent',
      cancel: 'Cancel',
      sending: 'Sending...',
      sendInvitation: 'Send Invitation',
      triggerLabel: 'Re-invite user',
      invitationSent: 'Invitation sent successfully',
    },
    acceptInvitation: {
      title: 'Organization Invitation',
      description: 'You have been invited to join an organization',
      organization: 'Organization:',
      role: 'Role:',
      expiresOn: 'This invitation expires on {date}',
      question: 'Would you like to accept this invitation and join the organization?',
      decline: 'Decline',
      accept: 'Accept Invitation',
      processing: 'Processing...',
    },
    redeemTrial: {
      title: 'Redeem Trial',
      description: 'Redeem a trial code for {orgName}',
      organization: 'Organization',
      trialCode: 'Trial Code',
      trialCodePlaceholder: '32 character trial code',
      trialCodeRequired: 'Trial code is required',
      trialCodeInvalid: 'Trial code must be 32 alphanumeric characters',
      cancel: 'Cancel',
      redeeming: 'Redeeming...',
      redeem: 'Redeem',
      trialRedeemedSuccessfully: 'Trial redeemed successfully',
      noOrganizationSelected: 'No organization selected',
      failedToRedeemTrial: 'Failed to redeem trial.',
    },
    syncConfirmation: {
      titleFull: 'Full Sync',
      titleReset: 'Reset and Full Sync',
      descriptionFull: 'You are about to perform a full sync operation.',
      descriptionReset: 'You are about to perform a destructive reset and full sync operation.',
      warningReset:
        'Warning: This is a destructive action and data will be temporarily unavailable.',
      warningResetOperations: 'This operation will:',
      warningResetOperation1: 'Delete existing data in the target',
      warningResetOperation2: 'Perform a complete full sync from the source',
      warningResetOperation3: 'May cause temporary data unavailability',
      warningFull: 'This will sync all data from the source.',
      warningFullDescription:
        'A full sync processes all records from the source, regardless of whether they have been synced before. This may take longer than an incremental sync and consume more resources.',
      acknowledgeReset:
        'I understand that this will delete all existing data and reset the sync state. I want to proceed with the reset and full sync.',
      cancel: 'Cancel',
      syncing: 'Syncing...',
      startFullSync: 'Start Full Sync',
      resetAndSync: 'Reset and Sync',
    },
    deleteSourceTable: {
      buttonText: 'Delete Source Table',
      title: 'Delete Source Table',
      description: 'Deleting a source table stops future syncs.',
      confirmationLabel: 'table name',
      sourceTableDeletedSuccessfully: 'Source table deleted successfully',
    },
    agreementAcceptance: {
      title: 'Legal Agreement',
      description: 'Please review and accept the agreement to continue.',
      loading: 'Loading...',
      loadError: 'Failed to load the agreement. Please try again later.',
      accept: 'Accept',
      accepting: 'Accepting...',
      decline: 'Decline',
      declining: 'Declining...',
      acceptError: 'Failed to accept the agreement. Please try again.',
      privacyPolicyTitle: 'Privacy Policy',
      termsOfUseTitle: 'Terms of Use',
      scrollToAccept: 'Scroll to the bottom to accept',
    },
  },
  allRuns: {
    title: 'All Runs',
    description: 'View all sync runs across all profiles in your organization',
    noOrganization: 'No organization selected',
    runHistory: 'Run History',
    filteredResults: 'Filtered results',
    filtersActive: 'filters active',
    filter: 'filter',
    allSyncRuns: 'All sync runs across profiles',
    noRunsFound: 'No sync runs found.',
    refresh: 'Refresh',
    liveUpdates: {
      label: 'Live Updates',
      tooltip: 'Automatically refresh data every 5 seconds. Auto-disables after 5 minutes.',
      autoDisabledMessage: 'Live updates have been automatically disabled after 5 minutes.',
      reenableButton: 'Re-enable',
    },
    showSkipped: {
      label: 'Show Skipped',
      tooltip: 'Skipped runs are hidden by default. Turn this on to include them in the list.',
    },
    table: {
      runId: 'Run ID',
      profile: 'Profile',
      sourceTable: 'Source Table',
      scheduled: 'Scheduled',
      started: 'Started',
      completed: 'Completed',
      state: 'State',
      duration: 'Duration',
      extractionStarted: 'Extraction Started',
      extractionCompleted: 'Extraction Completed',
      extractionDuration: 'Extraction Duration',
      loadStarted: 'Load Started',
      loadCompleted: 'Load Completed',
      loadDuration: 'Load Duration',
      rowsExtracted: 'Rows Extracted',
      rowsLoaded: 'Rows Loaded',
      rowsInserted: 'Rows Inserted',
      rowsUpdated: 'Rows Updated',
      rowsDeleted: 'Rows Deleted',
      rows: 'rows',
      progress: 'Progress',
      syncMode: 'Sync Mode',
      triggerSource: 'Trigger',
      region: 'Region',
      schemaChanges: 'Schema Changes',
      actions: 'Actions',
    },
    schemaChanges: {
      title: 'Schema Changes',
      columnsCreated: 'Columns Created',
      columnsWidened: 'Column Widening',
      failedMigrations: 'Failed Migrations',
      columnName: 'Column',
      sqlType: 'SQL Type',
      entity: 'Entity',
      fromType: 'From',
      toType: 'To',
      noChanges: 'No schema changes recorded for this run.',
      viewChanges: 'View {0} changes',
    },
    actions: {
      cancel: 'Cancel',
      cancelSuccess: 'Run cancelled successfully',
      cancelError: 'Failed to cancel run',
      copiedToClipboard: 'Copied to clipboard',
      failedToCopy: 'Failed to copy',
    },
    columnToggle: {
      columnsButton: 'Columns',
      toggleColumnsLabel: 'Toggle Columns',
      showAllColumns: 'Show all columns',
      extractionSection: 'Extraction',
      loadSection: 'Load',
      rows: 'Rows',
    },
    pagination: {
      page: 'Page',
      showing: 'Showing',
      runs: 'runs',
      previous: 'Previous',
      next: 'Next',
    },
    errors: {
      failedToLoad: 'Failed to load runs',
    },
    filters: {
      filterButton: 'Filter',
      profile: 'Profile',
      runState: 'Run State',
      sourceTable: 'Source Table',
      noProfilesAvailable: 'No profiles available',
      noSourceTablesAvailable: 'No source tables available',
      customSourceTable: 'Enter table name...',
      clearAll: 'Clear All',
      unknownProfile: 'Unknown Profile',
    },
  },
  help: {
    title: 'Help',
    description: 'Get support and find helpful resources',
    additionalResources: {
      title: 'Additional Resources',
      description: 'Find documentation, guides, and tutorials on our support site',
      content: 'Find additional resources on the support site',
      comingSoon: '(coming soon)',
    },
    chatWithSupport: {
      title: 'Chat with Support',
      description: 'Get help directly from our support team',
      comingSoon: 'Coming soon',
    },
  },
  billing: {
    title: 'Billing',
    description: 'Manage your subscription and entitlements',
    redeemTrialCode: 'Redeem Trial Code',
    subscribe: 'Subscribe',
    customerPortal: 'Customer Portal',
    manageSubscription: 'Manage Billing',
    customerPortalTooltip: 'Update subscription, view invoices, and change payment methods.',
    entitlementsTable: {
      title: 'Entitlements',
      type: 'Type',
      ruleType: 'Rule Type',
      profiles: 'Profiles',
      status: 'Status',
      expirationDate: 'End Date',
      noEntitlements: 'No entitlements found.',
      trial: 'Trial',
      paid: 'Subscription',
      default: 'Default',
      active: 'Active',
      expired: 'Expired',
      pastDue: 'Past Due',
      renewal: 'Renewal',
      enabled: 'Enabled',
      disabled: 'Disabled',
      never: 'Never',
      renewsOn: 'Renews {date}',
      expiresOn: 'Expires {date}',
      neverExpires: 'Never expires',
      ruleTypes: {
        minimum: 'Minimum',
        minimumTooltip: 'This entitlement grants at least {value} active profiles.',
        cumulative: 'Cumulative',
        cumulativeTooltip: 'This entitlement grants {value} additional active profiles.',
      },
    },
    pastDueAlert: {
      title: 'Payment Required',
      alertTitle: 'Payment Past Due',
      description: 'Your subscription payment is past due. Please update your payment method to avoid service interruption.',
    },
    trialExpirationAlert: {
      alertTitle: 'Trial Expiring Soon',
      description: 'Your trial expires in {days} days. Subscribe now to continue using all features.',
    },
    noEntitlementsAlert: {
      alertTitle: 'Subscription Required',
      description: 'You need an active subscription to sync profiles. Subscribe now to continue.',
    },
    planPicker: {
      title: 'Choose a plan',
      description:
        'Select how you would like to subscribe. You will be redirected to our payment provider to complete the purchase.',
      quantity: 'Quantity',
      total: 'Total',
      perMonth: '/month',
      perYear: '/year',
      savePercent: 'Save {percent}%',
      failedToLoad: 'Failed to load available plans',
      noPlans: 'No plans are currently available for your organization.',
      planUnavailable:
        'The selected plan is no longer available for your organization. The available plans have been refreshed.',
      checkoutError: 'Failed to start checkout. Please try again.',
    },
    invoiceHistory: {
      title: 'Invoices',
      date: 'Date',
      amount: 'Amount',
      statusLabel: 'Status',
      actions: 'Actions',
      noInvoices: 'No invoices found.',
      view: 'View',
      showing: 'Showing {count} of {total} invoices',
      previous: 'Previous',
      next: 'Next',
      status: {
        draft: 'Draft',
        open: 'Open',
        paid: 'Paid',
        uncollectible: 'Uncollectible',
        void: 'Void',
        unknown: 'Unknown',
      },
    },
    orgBilling: {
      title: 'Billing Information',
      billingAddress: 'Billing Address',
      paymentMethod: 'Payment Method',
      noAddress: 'No billing address on file',
      noPaymentMethod: 'No payment method on file',
      expires: 'Expires',
      failedToLoad: 'Failed to load billing information',
      paymentMethodTypes: {
        card: 'Card',
        acss_debit: 'Pre-authorized Debit (ACSS)',
        affirm: 'Affirm',
        afterpay_clearpay: 'Afterpay / Clearpay',
        alipay: 'Alipay',
        alma: 'Alma',
        amazon_pay: 'Amazon Pay',
        au_becs_debit: 'BECS Direct Debit (AU)',
        bacs_debit: 'Bacs Direct Debit',
        bancontact: 'Bancontact',
        billie: 'Billie',
        blik: 'BLIK',
        boleto: 'Boleto',
        card_present: 'Card (In-Person)',
        cashapp: 'Cash App Pay',
        crypto: 'Crypto',
        customer_balance: 'Account Balance',
        eps: 'EPS',
        fpx: 'FPX',
        giropay: 'giropay',
        grabpay: 'GrabPay',
        ideal: 'iDEAL',
        interac_present: 'Interac',
        kakao_pay: 'Kakao Pay',
        klarna: 'Klarna',
        konbini: 'Konbini',
        kr_card: 'Korean Card',
        link: 'Link',
        mb_way: 'MB WAY',
        mobilepay: 'MobilePay',
        multibanco: 'Multibanco',
        naver_pay: 'Naver Pay',
        nz_bank_account: 'NZ Bank Account',
        oxxo: 'OXXO',
        p24: 'Przelewy24',
        pay_by_bank: 'Pay by Bank',
        payco: 'PAYCO',
        paynow: 'PayNow',
        paypal: 'PayPal',
        paypay: 'PayPay',
        payto: 'PayTo',
        pix: 'Pix',
        promptpay: 'PromptPay',
        revolut_pay: 'Revolut Pay',
        samsung_pay: 'Samsung Pay',
        satispay: 'Satispay',
        sepa_debit: 'SEPA Direct Debit',
        sofort: 'Sofort',
        swish: 'Swish',
        twint: 'TWINT',
        us_bank_account: 'US Bank Account',
        wechat_pay: 'WeChat Pay',
        zip: 'Zip',
        unknown: 'Payment Method',
      },
    },
    manageAddOns: {
      title: 'Manage Add-ons',
      description: 'Modify your subscription add-ons',
      button: 'Manage Add-ons',
      currentAddOns: 'Current Add-ons',
      availableAddOns: 'Available Add-ons',
      noCurrentAddOns: 'You have no active add-ons',
      noAvailableAddOns: 'No additional add-ons available',
      add: 'Add',
      remove: 'Remove',
      perMonth: '/month',
      perYear: '/year',
      previewChanges: 'Preview Changes',
      confirmChanges: 'Confirm Changes',
      back: 'Back',
      noChanges: 'No changes to preview',
      preview: {
        title: 'Review Changes',
        description: 'Review the impact of your subscription changes',
        changesSummary: 'Changes Summary',
        dueToday: 'Due Today',
        dueTodayDescription: 'Prorated charges for the current billing period',
        nextRenewal: 'Next Renewal',
        nextRenewalDescription: 'Your new recurring charges',
        effectiveDate: 'Effective Date',
        immediateCharge: 'Immediate Charge',
        newRecurringTotal: 'New Recurring Total',
        wasQuantity: '(was {quantity})',
        newItem: '(new)',
        removed: '(removed)',
      },
      success: 'Add-ons updated successfully',
      errorPreview: 'Failed to preview changes',
      errorUpdate: 'Failed to update add-ons',
      pendingChanges: 'Pending downgrade will take effect on {date}',
      cancellationScheduled: 'Your subscription is scheduled for cancellation. Add-ons cannot be modified.',
    },
  },
  agents: {
    title: 'Agents',
    description: 'Manage self-hosted agents for your organization',
    addAgent: 'Add Agent',
    loadingAgents: 'Loading agents...',
    noAgentsFound: 'No agents found. Add an agent to get started.',
    table: {
      name: 'Name',
      status: 'Status',
      version: 'Version',
      lastHeartbeat: 'Last Heartbeat',
      registeredAt: 'Registered At',
      created: 'Created',
      actions: 'Actions',
      lastError: 'Last Error',
      viewDetails: 'View Details',
    },
    detailPage: {
      title: 'Agent Details',
      backButton: 'Back to Agents',
      infoCard: {
        title: 'Agent Information',
        status: 'Status',
        version: 'Version',
        lastHeartbeat: 'Last Heartbeat',
        registeredAt: 'Registered At',
        lastError: 'Last Error',
        noError: 'None',
      },
      logs: {
        title: 'Agent Logs',
        level: 'Level',
        source: 'Source',
        message: 'Message',
        timestamp: 'Received At',
        agentTime: 'Agent:',
        noLogs: 'No logs recorded.',
        loadMore: 'Load More',
        loading: 'Loading logs...',
        error: 'Failed to load logs',
        showing: 'Showing {count} entries',
      },
    },
    status: {
      Active: 'Active',
      Inactive: 'Inactive',
      Unregistered: 'Unregistered',
    },
    createDialog: {
      title: 'Add Agent',
      description: 'Create a new self-hosted agent for your organization',
      nameLabel: 'Agent Name',
      namePlaceholder: 'My Agent',
      cancel: 'Cancel',
      create: 'Create Agent',
      creating: 'Creating...',
      successTitle: 'Agent Created',
      successDescription: 'Your agent has been created. Copy the registration token below. This token will only be shown once.',
      registrationToken: 'Registration Token',
      tokenExpires: 'Token expires at',
      copyToken: 'Copy Token',
      tokenCopied: 'Token copied to clipboard',
      tokenWarning: 'This token will only be shown once. Make sure to copy it now.',
      close: 'Close',
    },
    editDialog: {
      title: 'Edit Agent',
      description: 'Update agent name and parallel tasks',
      nameLabel: 'Agent Name',
      namePlaceholder: 'My Agent',
      cancel: 'Cancel',
      save: 'Save Changes',
      saving: 'Saving...',
      success: 'Agent updated successfully',
      triggerLabel: 'Edit agent',
      workerCountLabel: 'Parallel tasks',
      workerCountDescription: "How many tasks the agent runs at the same time (1–10). Applied at the agent's next heartbeat. Default 4.",
      workerCountRange: 'Parallel tasks must be a whole number between 1 and 10',
    },
    deleteDialog: {
      title: 'Delete Agent',
      description: 'Are you sure you want to delete the agent "{name}"? This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete Agent',
      deleting: 'Deleting...',
      success: 'Agent deleted successfully',
      triggerLabel: 'Delete agent',
    },
    regenerateTokenDialog: {
      title: 'Regenerate Registration Token',
      description: 'Generate a new registration token for the agent "{name}". The previous token will be invalidated.',
      cancel: 'Cancel',
      regenerate: 'Regenerate Token',
      regenerating: 'Regenerating...',
      registrationToken: 'Registration Token',
      tokenExpires: 'Token expires at',
      copyToken: 'Copy Token',
      tokenCopied: 'Token copied to clipboard',
      tokenWarning: 'This token will only be shown once. Make sure to copy it now.',
      close: 'Close',
      triggerLabel: 'Regenerate registration token',
    },
    toggleStatus: {
      activate: 'Activate',
      deactivate: 'Deactivate',
      activated: 'Agent activated successfully',
      deactivated: 'Agent deactivated successfully',
    },
    relativeTime: {
      justNow: 'Just now',
      minutesAgo: '{count}m ago',
      hoursAgo: '{count}h ago',
      daysAgo: '{count}d ago',
      never: 'Never',
    },
    downloadLatest: 'Download Latest Agent',
    updateAvailable: 'Agent Update Available',
    updateAvailableDescription: 'The following agents are running an outdated version and should be updated:',
    versionOutdated: 'Update available',
    profileAgentOutdated: 'The agent assigned to this profile is running an outdated version. Please update to the latest version.',
    assignedAgent: 'Assigned Agent',
    agentDisabled: 'Agent Disabled',
    agentDisabledDescription: 'This agent is disabled. Scheduled tasks will be queued and processed when the agent is re-enabled.',
    health: {
      label: 'Health',
      Online: 'Online',
      Suspect: 'Checking',
      Offline: 'Offline',
      offlineSince: 'Offline since {time}',
    },
    healthCheck: {
      confirm: 'Confirm health',
      started: 'Health check started',
      checking: 'Checking…',
      potentialIssue: 'Potential health issue',
      likelyIssue: 'Likely health issue',
      healthy: 'Healthy',
      busy: 'Busy – check queued behind a running task',
      unresponsive: 'Not responding – marked offline',
      inconclusive: 'Check could not be completed',
      lastResult: 'Last health check',
    },
    agentOffline: {
      title: 'Syncs paused – agent offline',
      profileDescription: 'The agent "{agent}" that loads this profile\'s target has been offline since {time}. Nothing is synced until it is back online; syncs resume automatically.',
      dashboardDescription: '{count} agent(s) offline. Profiles that use them are paused until they are back online.',
      viewAgents: 'View agents',
    },
    errors: {
      failedToLoadAgents: 'Failed to load agents',
    },
  },
  organizationMembers: {
    title: 'Organization Members',
    description: 'Manage members and their roles in your organization',
    inviteNewUser: 'Invite New User',
    loadingMembers: 'Loading members...',
    noMembersFound: 'No members found. Invite users to get started.',
    organizationNotFound: 'Organization not found',
    table: {
      email: 'Email',
      role: 'Role',
      status: 'Status',
      joined: 'Joined',
      expires: 'Expires',
      actions: 'Actions',
    },
    roles: {
      Administrator: 'Administrator',
      Operator: 'Operator',
      Reader: 'Reader',
      Billing: 'Billing Manager',
    },
    status: {
      Accepted: 'Accepted',
      Invited: 'Invited',
      Declined: 'Declined',
      Expired: 'Expired',
    },
    indefinite: 'Indefinite',
  },
  userContext: {
    noAccount: 'No account available',
    authRequired: 'Interactive authentication required',
    initFailed: 'User initialization failed',
    loading: 'Loading...',
    mustUseProvider: 'useUser must be used within a UserProvider',
  },
  authenticationError: {
    title: 'Authentication Required',
    description:
      'Your session has expired or authentication failed. Please sign in again to continue.',
    signInButton: 'Sign in again',
  },
  errors: {
    common: {
      title: 'Error Loading Data',
      retry: 'Retry',
      failedToLoad: 'Failed to load data',
      networkError: 'Network error - please check your connection',
      serverError: 'Server error - please try again later',
      unauthorized: 'Authentication required - please sign in again',
      forbidden: 'Access denied - insufficient permissions',
      notFound: 'Resource not found',
      unknown: 'An unexpected error occurred',
    },
    profiles: {
      failedToLoadProfiles: 'Failed to load profiles',
      failedToLoadProfileDetails: 'Failed to load profile details',
      failedToLoadSchedules: 'Failed to load schedules',
      failedToLoadSourceTables: 'Failed to load source tables',
      failedToLoadRuns: 'Failed to load sync runs',
    },
    entitlements: {
      failedToLoadEntitlements: 'Failed to load subscription information',
    },
    members: {
      failedToLoadRoles: 'Failed to load organization roles',
      failedToLoadMembers: 'Failed to load organization members',
    },
    codes: {
      Success: 'The sync operation completed successfully.',
      BadRequest:
        'The request was invalid or malformed. Please verify your configuration settings and try again.',
      Unauthorized:
        'Authentication is required to perform this operation. Please sign in again or verify your credentials.',
      Forbidden:
        'You do not have permission to perform this operation. Please contact your administrator to request access.',
      NotFound:
        'The requested resource could not be found. Verify that the profile, source, or target still exists.',
      Conflict:
        'This operation conflicts with an existing resource. Another sync may be in progress or the resource already exists.',
      InternalServerError:
        'An unexpected server error occurred. Please try again later. If the problem persists, contact support.',
      DatabaseError:
        'A database operation failed. Please check your database configuration and try again.',
      ServiceUnavailable:
        'The service is temporarily unavailable. Please wait a few minutes and try again.',
      DataExtractionFailed:
        'Failed to extract data from the source. Verify the source connection settings and that the service principal has read access to the Dataverse entity.',
      SchemaExtractionFailed:
        'Failed to extract schema information from the source. Verify the service principal has permission to read entity metadata in Dataverse.',
      CheckpointRetrievalFailed:
        'Failed to retrieve sync checkpoint information. The sync state may be corrupted. Try performing a Reset & Full Sync to restore the sync state.',
      TargetLoadFailed:
        'Failed to load data into the target database. Verify the target connection settings and that the service principal has write permissions on the target schema.',
      DataTransformationFailed:
        'Failed to transform data during sync. The source data may contain values incompatible with the target schema. Check the external error message for details.',
      SourceConnectionFailed:
        'Failed to connect to the Dataverse source. Verify the Base URL, Client ID, and Client Secret are correct and the application is registered in the correct tenant.',
      TargetConnectionFailed:
        'Failed to connect to the Azure SQL target. Verify the server name, database name, and credentials. Ensure the firewall allows connections from Azure services.',
      RateLimitExceeded:
        'The Dataverse API rate limit was exceeded. The sync will automatically retry. If this persists, consider reducing sync frequency or contacting Microsoft support.',
      TimeoutError:
        'The operation timed out before completing. This may indicate network issues or a slow response from the source or target system.',
      NetworkError:
        'A network error occurred. Please check your internet connection and verify that the source and target endpoints are accessible.',
      OrchestrationTimeout:
        'The sync operation exceeded the maximum allowed execution time. For large data volumes, consider syncing fewer tables or performing an initial sync during off-peak hours.',
      OrchestrationCancelled:
        'The sync was cancelled, either manually or because the associated profile or entity was deleted.',
      OrchestrationOrphaned:
        'The sync operation was unexpectedly dropped before it could complete. Please retry the sync.',
      InvalidConfiguration:
        'The profile configuration is invalid. Please review your source, target, and table settings to ensure they are complete and correct.',
      TargetInitialSyncConflict:
        'The target table already contains data from a previous sync. Either delete the existing data from the target table or use Reset & Full Sync to start fresh.',
      DataverseCallFailed:
        'A call to the Dataverse API failed unexpectedly. Check the external error message for details. If the issue persists, verify your Dataverse environment is healthy.',
      DataverseAuthenticationFailed:
        'Authentication to Dataverse failed. Verify the Client ID and Client Secret are correct, the secret has not expired, and the application is registered in the Dataverse tenant.',
      DataverseChangeTrackingNotEnabled:
        'Change tracking is not enabled for this Dataverse entity. Enable change tracking in the Dataverse admin center to allow incremental syncs.',
      DataverseEntityNotFound:
        'The Dataverse entity was not found. Verify the entity logical name is correct and the entity exists in your Dataverse environment.',
      DataversePermissionDenied:
        'The service principal does not have permission to access this Dataverse entity. Grant the application appropriate security roles in the Dataverse admin center.',
      DataverseInvalidEntityConfiguration:
        'The Dataverse entity configuration is invalid. The entity may be missing a primary key or have an unsupported structure.',
      DataverseSecretRetrievalFailed:
        'Failed to retrieve credentials from the secure store. Please re-enter your Client Secret in the source configuration.',
      LockAcquisitionFailed:
        'Failed to acquire a lock on the target database. Another sync may be in progress. Wait for the current sync to complete before retrying.',
      AzureSqlAuthenticationFailed:
        'Authentication to Azure SQL failed. Verify the Tenant ID, Client ID, and Client Secret are correct. Ensure the service principal is added as a database user with appropriate permissions.',
      AzureSqlConnectionTimeout:
        'Connection to Azure SQL timed out. The database server may be unreachable, paused, or experiencing high load. Check the server status in the Azure portal.',
      AzureSqlPermissionDenied:
        'The service principal does not have sufficient permissions on the Azure SQL database. Grant the service principal db_datareader and db_datawriter roles on the target database.',
      AzureSqlSchemaMigrationFailed:
        'Failed to create or update the database schema. The service principal may lack permission to create tables, or the schema change conflicts with existing objects.',
      AzureSqlDataImportFailed:
        'Failed to import data into Azure SQL. Check that the data types are compatible and the service principal has INSERT permissions on the target tables.',
      AzureSqlMergeFailed:
        'The data merge operation failed. This may be due to data type mismatches, constraint violations, or SQL syntax errors. Check the external error message for details.',
      AzureSqlConstraintViolation:
        'Data violates a database constraint (foreign key, unique, or check constraint). The source data may contain invalid references or duplicate keys.',
      AzureSqlDeadlock:
        'A deadlock was detected during the sync operation. The operation will be retried automatically. If this persists, consider scheduling syncs during off-peak hours.',
      AzureSqlDatabaseNotFound:
        'The specified database was not found on the Azure SQL server. Verify the database name is correct and the database has not been deleted or renamed.',
      AzureSqlServerNotFound:
        'The Azure SQL server could not be reached. Verify the server name is correct, the server exists, and network connectivity allows the connection.',
      AzureSqlTransactionTimeout:
        'A database transaction timed out. The operation took too long to complete, possibly due to lock contention or resource constraints. Consider scaling up the database tier.',
      AzureSqlInsufficientResources:
        'The Azure SQL database has insufficient resources (CPU, memory, or DTUs) to complete the operation. Consider scaling up to a higher service tier.',
      AzureSqlSchemaMetadataNotFound:
        'Required schema metadata was not found. This may indicate a sync state issue. Try performing a Reset & Full Sync to regenerate the schema.',
      AzureSqlPrimaryKeyStructureChanged:
        'The primary key structure of the source entity has changed and cannot be applied to the existing target table. Delete the target table or use Reset & Full Sync.',
      AzureSqlUnsafeSchemaChange:
        'The source schema change cannot be safely applied. Only widening column types and making columns nullable are supported. Other changes require a Reset & Full Sync.',
      AzureSqlMergeTimeout:
        'The data merge operation timed out. This typically occurs when syncing large volumes of data to a low-tier database. Consider scaling up your database tier or reducing the data volume per sync.',
      AzureSqlFirewallBlocked:
        'Connection blocked by Azure SQL firewall. Add the Data Mission static IP ({ip}) to your server\'s firewall allow-list.',
      AzureSqlSchemaNotFound:
        'The specified schema does not exist in the target database. Create the schema in Azure SQL or change the schema name in your target configuration.',
      AgentTaskTimedOut:
        'The self-hosted agent did not complete the task within the configured timeout. Verify the agent is running and has network access to the source and target.',
      AgentTaskFailed:
        'The self-hosted agent reported a task failure. Ensure the agent software is up to date and check the agent logs for details.',
      UnsupportedTaskType:
        'The agent received a task type it does not support. Ensure the agent software is up to date.',
      AgentTaskNotPickedUp:
        'The self-hosted agent did not start the task within 10 minutes while it was not busy with other work. Verify the agent service is running and connected.',
      SupportServiceUnavailable: 'Support is temporarily unavailable. Please try again in a few minutes.',
      SupportServiceMisconfigured: 'Support is temporarily unavailable. Please try again later or contact us if the problem persists.',
      // SQL Server numeric error codes
      '40615':
        'Your IP address is not allowed to connect to the server. Please add this IP to the Azure SQL firewall rules.',
      '-2': 'Connection timeout. The server may be unreachable or firewall rules may be blocking access.',
      '-1': 'Connection timeout. The server may be unreachable or firewall rules may be blocking access.',
      '18456':
        'Login failed. Please verify your credentials are correct and have access to the database.',
      '4060':
        'Cannot access the database. Please verify the database exists and the service principal has access.',
      '297': 'The service principal does not have permission to access the database.',
      '916': 'The service principal does not have permission to access the database.',
      '53': 'Network error connecting to the server. The server may be unreachable or firewall rules may be blocking access.',
      '10054':
        'Network error connecting to the server. The server may be unreachable or firewall rules may be blocking access.',
      '10060':
        'Network error connecting to the server. The server may be unreachable or firewall rules may be blocking access.',
      '10061':
        'Network error connecting to the server. The server may be unreachable or firewall rules may be blocking access.',
      '20': 'The instance is not configured to accept connections. Please verify the server name and connectivity.',
      '1202':
        'Failed to connect to target. The Azure SQL server could not be reached. Please verify your connection settings.',
      '1501':
        'Authentication failed. Please verify your Tenant ID, Client ID, and Client Secret are correct and the service principal has access to the database.',
      '1502':
        'Connection timeout. The Azure SQL server may be unreachable or experiencing high load.',
      '1503':
        'The service principal does not have sufficient SQL permissions for sync operations. Please grant the necessary permissions.',
      '1509':
        'Database not found. Please verify the database name exists and the service principal has access.',
      '1510':
        'Server not found or network error. Please verify the Azure SQL server name and network connectivity.',
      '1517':
        "Connection blocked by firewall. Please configure your Azure SQL server firewall to either allow Azure services or add the IP addresses for Azure's Canada Central and your selected profile region.",
      '1518':
        'Schema not found. The specified schema does not exist in the database. Please verify the schema name or create it.',
      // Dataverse connection error codes
      DataverseHostnameNotFound:
        'The Dataverse hostname could not be resolved. Please verify the Base URL is correct and the organization exists.',
      DataverseInvalidUrl:
        'Invalid Dataverse organization URL format. Please check that the Base URL is a valid HTTPS URL.',
      DataverseInvalidClientId:
        'Invalid Client ID. The application identifier is not recognized. Please verify your Azure AD application registration.',
      DataverseInvalidClientSecret:
        'Invalid Client Secret. The client secret is incorrect or has expired. Please verify or regenerate your client secret.',
      DataverseInvalidAppRegistration:
        'Invalid application registration. The application does not have access to this Dataverse instance or is not registered in the tenant.',
      DataverseInvalidTenant:
        'Unable to determine authentication tenant from Dataverse URL. Please verify the Base URL format is correct.',
      DataverseConnectionFailed:
        'Failed to connect to Dataverse instance. Please verify the Base URL is correct and the instance is accessible.',
      DataversePermissionDeniedMetadata:
        'Missing permission to read entity metadata. The service principal cannot retrieve schema information from Dataverse.',
      DataversePermissionDeniedData:
        'Missing permission to read entity data. The service principal cannot read records from Dataverse entities.',
      DataverseVersionStampExpired:
        'The table has not been synced in too long and the incremental sync state has expired. A Reset & Full Sync is required to resume syncing.',
      DataverseInvalidRequest:
        'An error occurred while reading data from Dataverse. Check the external error message for details. If the issue persists, verify your entity configuration and permissions.',
      // Business Central error codes
      BcCallFailed:
        'A call to the Business Central API failed unexpectedly. Check the external error message for details. If the issue persists, verify your Business Central environment is healthy.',
      BcAuthenticationFailed:
        'Authentication to Business Central failed. Verify the Client ID and Client Secret are correct, the secret has not expired, and the application is registered in the correct tenant.',
      BcInvalidClientId:
        'Invalid Client ID. The application identifier is not recognized in Azure AD. Please verify your application registration.',
      BcInvalidClientSecret:
        'Invalid Client Secret. The client secret is incorrect or has expired. Please verify or regenerate your client secret.',
      BcEnvironmentNotFound:
        'The Business Central environment was not found. Verify the environment name is correct and the environment exists in your tenant.',
      BcCompanyNotFound:
        'The Business Central company was not found. Verify the company ID or company name is correct and that the company exists in the selected environment.',
      BcPermissionDenied:
        'The service principal does not have permission to access Business Central. Ensure the application has the required API permissions and is consented in the tenant.',
      BcEntityNotFound:
        'The Business Central entity or API page was not found. Verify the entity name is correct and the API page is published in Business Central.',
      BcNotFound:
        'Business Central reported that a requested resource was not found, but it did not identify whether the missing resource was the environment, company, entity, or record. Check the external error details for the exact request.',
      BcInvalidRequest:
        'Business Central rejected the request. Check the external error message for details about which field or parameter is invalid.',
      BcTenantUnavailable:
        'The Business Central tenant is currently unavailable. The environment may be undergoing maintenance or migration. Please try again later.',
      // Salesforce error codes
      SalesforceCallFailed:
        'A Salesforce API call failed unexpectedly. Check the error details. If the issue persists, verify your Salesforce environment is healthy.',
      SalesforceAuthenticationFailed:
        'Authentication to Salesforce failed. Verify the Instance URL, Client ID, and Client Secret are correct and the Connected App is active.',
      SalesforceTokenRequestFailed:
        'Failed to obtain a Salesforce access token. Verify the Instance URL is correct and the Connected App is configured for the client credentials flow.',
      SalesforceInvalidClientCredentials:
        'The Salesforce Client ID or Client Secret is invalid. Verify the Consumer Key and Consumer Secret from your Connected App.',
      SalesforceInstanceUrlInvalid:
        'The Salesforce instance URL is invalid or unreachable. Verify the URL is a valid Salesforce org URL (e.g. https://mycompany.my.salesforce.com).',
      SalesforceRateLimitExceeded:
        'The Salesforce API rate limit was exceeded. The sync will automatically retry. If this persists, consider reducing sync frequency.',
      SalesforceBulkApiUnavailable:
        'The Salesforce Bulk API 2.0 is not available for this org. Ensure the Connected App has the required OAuth scopes and the running user has the "API Enabled" permission.',
      SalesforceBulkJobFailed:
        'The Salesforce bulk query job failed. Check the error details. Verify the object is queryable and the running user has read access.',
      SalesforceBulkJobTimedOut:
        'The Salesforce bulk query job timed out. The object may contain a very large volume of data. Consider reducing the number of synced fields.',
      SalesforceEntityNotFound:
        'The Salesforce object (SObject) was not found. Verify the API name is correct and the object exists in your org.',
      SalesforceApiScopeNotGranted:
        'The Salesforce token does not have API access. Ensure the Connected App includes the "api" OAuth scope and the Run As user has the "API Enabled" system permission.',
      SalesforceClientCredentialsNotConfigured:
        'The client credentials flow is not configured for this Connected App. In Salesforce Setup, go to App Manager → Edit Policies → Client Credentials Flow and assign a Run As user.',
      SalesforceClientIdNotRecognized:
        'The Salesforce Client ID (Consumer Key) is not recognized. Verify the Instance URL matches the org where the Connected App is installed and the app is active.',
      unknown: 'An unknown error occurred. Please try again or contact support.',
    },
  },
  systemNotices: {
    title: 'System Notices',
    noNotices: 'No active notices',
    showMore: 'Show more',
    showLess: 'Show less',
    acknowledge: 'Acknowledge',
    acknowledgeForOrg: 'Acknowledge for organization',
    acknowledgedSuccessfully: 'Notice acknowledged successfully',
    noPermissionToAcknowledge: 'You do not have permission to acknowledge this notice',
    failedToLoad: 'Failed to load system notices',
  },
};
